import React, { useMemo, useState, useEffect, useRef } from "react";
import { connect } from "react-redux";
import { useHistory } from "react-router-dom";
import CustomModal from "../../../components/CustomModal/CustomModal";
import CustomTable from "../../../components/CustomTable/CustomTable";
import Pagination from "../../../components/CustomTable/Pagination";
import CustomSeparator from "../../../components/Common/CustomSeparator";
import CheckRender from "../../../components/Common/CheckRender";
import CustomAlert from "../../../components/Common/CustomAlert";
import Spinner from "../../../components/Common/Spinner";
import CalculadoraOxigeno from "./CalculadoraOxigeno";
import useEmpresa from "../../../hooks/useEmpresa";
import { useUpdateEffect } from "../../../hooks/LifeCycle";
import { toNumber, formatRD, displayRD } from "../utils/HospitalizacionUtils";
import { isObjectEmpty } from "../../../utils/ObjectUtil";
import { isEmptyValue } from "../../../utils/StringUtil";
import EgresosActions from "../../../stores/hospitalizacion/egresos/Actions";
import { RAMO_HOSPITALIZACION, TIPO_CONTRATO, COBERTURA_OXIGENO, COBERTURA_HABITACION, COBERTURA_ANESTESIA, COBERTURA_PARTO_NORMAL, COBERTURA_CESAREA, esCoberturaMontoAbierto, SERVICIO_AMBULATORIO, SERVICIO_MATERNIDAD, SERVICIO_PARTO, SERVICIO_CESAREA, LITROS_REGEX, MONTO_REGEX } from "../utils/HospitalizacionConstants";
import {
  PlusCircleOutlined,
  DeleteOutlined,
  SearchOutlined,
  InfoCircleOutlined,
  LoadingOutlined,
} from "@ant-design/icons";

import {
  Header,
  Title,
  GreenButton,
  TableOuter,
  ScrollX,
  CoveragesTable,
  Input,
  Select,
  ReadonlyInput,
  IconBtn,
  ActionsCell,

  FooterRow,
  VoucherGroup,
  VoucherButton,
  TotalsBox,
  TotalValue,
  ModalBody,
  ModalHeader,
  ModalTitle,
  ModalSearch,
  SelectBtn,
  ActionsBar,
  SecondaryAction,
  PrimaryAction,
} from "../styles/CoberturasCierreCuentaTablaStyles";

const ITEMS_PER_PAGE = 10;

const calcularDifNoCubierta = (row) => {
  const difNoCubierta = Number(row?.diferenciaNoCubierta) || 0;
  const montoCoaseguro = Number(row?.montoCoaseguro) || 0;
  
  if (difNoCubierta !== 0) return difNoCubierta;
  if (montoCoaseguro !== 0) return montoCoaseguro;
  return 0;
};

/**
 * Calcula la distribución ARS/Afiliado considerando el límite por caso.
 * Si el total de la cuenta (otras coberturas) + montoReclamado supera el límite,
 * solo la porción que cabe en el límite se calcula normalmente y el excedente
 * va completo a diferencia no cubierta.
 */
const calcularDistribucionConLimiteCaso = (montoReclamado, porcientoCoaseguro, totalCuentaOtras, limiteCaso) => {
  if (!limiteCaso || limiteCaso <= 0) {
    // Límite agotado/no disponible: todo el monto se carga al asegurado.
    return { montoPagoCobertura: 0, diferenciaNoCubierta: montoReclamado, excedenteLimite: montoReclamado, limiteAlcanzado: true };
  }

  const espacioDisponible = limiteCaso - totalCuentaOtras;

  // Límite ya alcanzado: todo va a diferencia no cubierta
  if (espacioDisponible <= 0) {
    return { montoPagoCobertura: 0, diferenciaNoCubierta: montoReclamado, excedenteLimite: montoReclamado, limiteAlcanzado: true };
  }

  // Cabe completamente en el límite: cálculo normal
  if (montoReclamado <= espacioDisponible) {
    const difNoCubierta = montoReclamado * (porcientoCoaseguro / 100);
    const montoPagarARS = montoReclamado - difNoCubierta;
    return { montoPagoCobertura: montoPagarARS, diferenciaNoCubierta: difNoCubierta, excedenteLimite: 0, limiteAlcanzado: false };
  }

  // Parcial: parte entra en el límite, parte es excedente
  const montoEnLimite = espacioDisponible;
  const excedente = montoReclamado - montoEnLimite;
  const difNoCubiertaDelLimite = montoEnLimite * (porcientoCoaseguro / 100);
  const montoPagarARS = montoEnLimite - difNoCubiertaDelLimite;
  const difNoCubiertaTotal = difNoCubiertaDelLimite + excedente;

  return { montoPagoCobertura: montoPagarARS, diferenciaNoCubierta: difNoCubiertaTotal, excedenteLimite: excedente, limiteAlcanzado: false };
};

const pickCoberturas = (raw) => {
  return (
    raw?.data?.coberturasCierresCuentas ||
    raw?.coberturasCierresCuentas ||
    raw?.data?.data?.coberturasCierresCuentas ||
    raw?.coberturas ||
    raw?.data?.coberturas ||
    []
  );
};

const pickTotalRegistros = (raw) => {
  return raw?.data?.totalRegistros ?? raw?.totalRegistros ?? 0;
};

const CoberturasCierreCuentaTabla = ({
  coberturasDisponibles = [],
  consultarCoberturasCierreCuentaData,
  consultarCoberturasCierreCuentaLoading,
  authUserData,
  onConsultarCoberturas,
  onCoberturasChange,
  eliminarCobertura,
  eliminarCoberturaData,
  eliminarCoberturaLoading,
  eliminarCoberturaError,
  limpiarEliminarCobertura,
  validarCobertura,
  validarCoberturaData,
  validarCoberturaError,
  limpiarValidarCobertura,
  calcularOxigeno,
  calcularOxigenoData,
  limpiarCalcularOxigeno,
  anioFormulario,
  secuencialFormulario,
  consultarIngresosPssData,
  encabezado,
  formularioBloqueado = false,
  tipoPssNoMedicoData,
  consultarReclamantes,
  consultarReclamantesData,
  consultarReclamantesLoading,
  consultarReclamantesError,
  limpiarConsultarReclamantes,
  fechaEgresoFormulario,
  tipoServicioFormulario,
  motivoEgresoFormulario,
  validarCamposRequeridos,
  validarIngresoExistente,
  validarIngresoExistenteData,
  validarIngresoExistenteLoading,
  validarIngresoExistenteError,
  limpiarValidarIngresoExistente,
  enviarReclamacion,
  enviarReclamacionData,
  enviarReclamacionLoading,
  enviarReclamacionError,
  limpiarEnviarReclamacion,
  tieneDetalle = false,
}) => {
  const history = useHistory();
  const empresa = useEmpresa();
  const primary = empresa?.COLOR_PRINCIPAL || "#00a0df";

  const prestadorCodigo = authUserData?.prestador?.codigo ?? "";
  const prestadorNombre = authUserData?.prestador?.nombre ?? "";
  const tipoPss = authUserData?.prestador?.tipoPss ?? "";

  const [formularioBloqueadoPorCobertura, setFormularioBloqueadoPorCobertura] = useState(false);
  const [codigoCoberturaBuscando, setCodigoCoberturaBuscando] = useState(null);
  const debounceCodigoCoberturaRef = useRef(null);

  const isConvertido = encabezado?.estatusDesc === "CONVERTIDO";
  const isPdssNoValido = encabezado?.checkPdss === "S";
  const esVigente = encabezado?.estatusDesc === "VIGENTE";
  const tieneFechaEgreso = encabezado?.fechaSalida || fechaEgresoFormulario;
  const isDisabled = isConvertido || isPdssNoValido || formularioBloqueado || formularioBloqueadoPorCobertura || !tieneFechaEgreso;
  
  // Nuevo: estado de carga de coberturas para bloquear campo "Cód. Proc."
  const [coberturasCargando, setCoberturasCargando] = useState(false);

  const esPssNoMedico = tipoPss === "NO_MEDICO";
  const tipoReclamantePss = tipoPssNoMedicoData?.tipoReclamante || tipoPssNoMedicoData?.TipoReclamante || "";
  const esPaquete = tipoReclamantePss.toUpperCase() === "PAQUETE";

  const [rows, setRows] = useState([]);
  const [showAlert, setShowAlert] = useState(false);
  const [alertConfig, setAlertConfig] = useState({});
  const [triggerValidacionCobertura, setTriggerValidacionCobertura] = useState(false);
  const [errorMontoReclamado, setErrorMontoReclamado] = useState(false);
  const debounceTimerRef = useRef(null);
  const coberturaEliminandoRef = useRef(null);
  const [topeAnestesia, setTopeAnestesia] = useState(null); // Tope para cobertura 1096 basado en monto de 903/910
  const topeAnestesiaAnteriorRef = useRef(null); // Guardar el tope anterior para detectar cambios
  
  // Estados para los modales de voucher
  const [showVoucherModal, setShowVoucherModal] = useState(false);
  const [voucherConfig, setVoucherConfig] = useState({
    tipo: null, // 'termico', 'matricial', 'medico'
    reclamantes: [],
    reclamanteSeleccionado: null,
    esMatricial: false, // solo para voucher médico
  });
  const [descargandoVoucher, setDescargandoVoucher] = useState(false);
  const filaValidandoRef = useRef(null); // Guardar la fila que se está validando
  const montoReclamadoEnviadoRef = useRef(null); // Monto enviado en la validación (evita sobrescribir cambio manual)
  const lastMontoReclamadoEditRef = useRef({ cobertura: null, secuencia: null, value: null }); // Último valor editado en input para onBlur
  const incluirDraftEnEnvioRef = useRef(false); // Al enviar: si el draft tiene cobertura válida, se incluye en el payload
  const limiteCasoAlertMostradaRef = useRef(false); // Evitar mostrar la alerta de límite por caso más de una vez
  const [payloadEnvioReclamacion, setPayloadEnvioReclamacion] = useState(null); // Guardar el payload para enviar después de la validación

  // Los vouchers solo se deshabilitan si NO hay coberturas o si está bloqueado por cobertura no válida
  // pero SI se permiten en ingresos convertidos/cerrados
  const vouchersDisabled = rows.length === 0 || formularioBloqueadoPorCobertura;

  // Función para buscar reclamante con debounce
  const buscarReclamanteDebounced = (codigoReclamante, tipoReclamante) => {
    // Limpiar timer anterior
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Si está vacío, no hacer nada
    if (!codigoReclamante || String(codigoReclamante).trim() === "") {
      return;
    }

    // Esperar 800ms después de que el usuario deje de escribir
    debounceTimerRef.current = setTimeout(() => {
      const payload = {
        TipoReclamante: tipoReclamante || "NO_MEDICO",
        Reclamante: Number(codigoReclamante),
        Compania: 30,
      };
      
      if (typeof consultarReclamantes === "function") {
        consultarReclamantes(payload);
      }
    }, 800);
  };

  const getInitialDraft = () => {
    const fechaActual = new Date().toISOString();
    return {
      ano: 0,
      secuencial: 0,
      tipoCobertura: 2,
      cobertura: 0,
      descripcionCobertura: "",
      frecuencia: 1,
      frecuenciaMaxima: null, // Para cobertura 1064: frecuencia máxima permitida
      montoReclamadoCobertura: 0,
      ramoIngreso: RAMO_HOSPITALIZACION,
      codigoCompania: 0,
      codigoCentro: 0,
      limiteCobertura: 0,
      porcientoCobertura: 0,
      montoPagoCobertura: 0,
      montoPagoAfiliado: 0,
      excedenteCopago: 0,
      servicio: 0,
      fechaServicio: fechaActual,
      secuencia: 1,
      reserva: 0,
      estatus: 61,
      tipoAfiliado: "NO_MEDICO",
      usuarioTransaccion: "",
      fechaTransaccion: fechaActual,
      fechaIngresoCobertura: fechaActual,
      usuarioIngresoCobertura: "",
      nombreReclamante: "",
    };
  };

  const [draft, setDraft] = useState(getInitialDraft);

  useEffect(() => {
    setDraft((p) => ({
      ...p,
      codigoCentro: Number(prestadorCodigo) || 0,
      usuarioTransaccion: prestadorNombre || "",
      usuarioIngresoCobertura: prestadorNombre || "",
      nombreReclamante: prestadorNombre || "",
    }));
  }, [prestadorCodigo, prestadorNombre]);

  // Efecto para manejar la respuesta de consultar reclamantes
  useUpdateEffect(() => {
    if (consultarReclamantesLoading) return;

    // Función para restaurar al PSS logueado
    const restaurarPssLogueado = () => {
      setDraft((p) => ({
        ...p,
        tipoAfiliado: tipoPss || "NO_MEDICO",
        codigoCentro: Number(prestadorCodigo) || 0,
        nombreReclamante: prestadorNombre || "",
      }));
    };

    if (consultarReclamantesError) {
      setAlertConfig({
        type: "error",
        title: "¡Error!",
        message: "Código de reclamante inválido",
        onConfirm: () => {
          setShowAlert(false);
          restaurarPssLogueado();
        },
      });
      setShowAlert(true);
      limpiarConsultarReclamantes?.();
      return;
    }

    const reclamantes = consultarReclamantesData?.reclamantes || [];
    if (reclamantes.length === 0 && !isObjectEmpty(consultarReclamantesData)) {
      setAlertConfig({
        type: "error",
        title: "¡Error!",
        message: "Código de reclamante inválido",
        onConfirm: () => {
          setShowAlert(false);
          restaurarPssLogueado();
        },
      });
      setShowAlert(true);
    } else if (reclamantes.length > 0) {
      const reclamante = reclamantes[0];
      setDraft((p) => ({
        ...p,
        codigoCentro: reclamante.codigo,
        nombreReclamante: reclamante.nombre,
      }));
      // Disparar validación de cobertura después de actualizar el draft
      setTriggerValidacionCobertura(true);
    }

    limpiarConsultarReclamantes?.();
  }, [consultarReclamantesData, consultarReclamantesError, consultarReclamantesLoading]);

  useEffect(() => {
    if (!encabezado || !anioFormulario || !secuencialFormulario) return;

    const poliza = encabezado?.poliza || "";
    const polizaParts = poliza.split("-");
    const ramo = polizaParts.length >= 2 ? Number(polizaParts[1]) : RAMO_HOSPITALIZACION;

    setDraft((p) => ({
      ...p,
      ano: Number(anioFormulario) || 0,
      secuencial: Number(secuencialFormulario) || 0,
      ramoIngreso: ramo,
      codigoCompania: Number(empresa?.CODIGO) || 0,
      servicio: Number(tipoServicioFormulario) || 0,
    }));
  }, [encabezado, anioFormulario, secuencialFormulario, empresa?.CODIGO]);

  const limpiarDraft = () => {
    const fechaActual = new Date().toISOString();
    const poliza = encabezado?.poliza || "";
    const polizaParts = poliza.split("-");
    const ramo = polizaParts.length >= 2 ? Number(polizaParts[1]) : RAMO_HOSPITALIZACION;

    // Limpiar error de monto reclamado
    setErrorMontoReclamado(false);

    setDraft({
      ano: Number(anioFormulario) || 0,
      secuencial: Number(secuencialFormulario) || 0,
      tipoCobertura: 2,
      cobertura: 0,
      descripcionCobertura: "",
      frecuencia: 1,
      frecuenciaMaxima: null,
      montoReclamadoCobertura: 0,
      ramoIngreso: ramo,
      codigoCompania: Number(empresa?.CODIGO) || 0,
      codigoCentro: Number(prestadorCodigo) || 0,
      limiteCobertura: 0,
      porcientoCobertura: 0,
      montoPagoCobertura: 0,
      montoPagoAfiliado: 0,
      diferenciaNoCubierta: 0,
      montoCoaseguro: 0,
      excedenteCopago: 0,
      servicio: Number(tipoServicioFormulario) || 0,
      fechaServicio: fechaActual,
      secuencia: 1,
      reserva: 0,
      estatus: 61,
      tipoAfiliado: "NO_MEDICO",
      usuarioTransaccion: prestadorNombre || "",
      fechaTransaccion: fechaActual,
      fechaIngresoCobertura: fechaActual,
      usuarioIngresoCobertura: prestadorNombre || "",
      nombreReclamante: prestadorNombre || "",
    });
  };

  const construirPayloadValidacion = (cobertura, frecuencia, montoReclamado, filaData) => {
    if (!encabezado || !anioFormulario || !secuencialFormulario) return null;

    const codigoCobertura = Number(cobertura?.cobertura || draft.cobertura || 0);
    const freq = Number(frecuencia || draft.frecuencia || 1);
    const monto = montoReclamado != null ? Number(montoReclamado) : Number(draft.montoReclamadoCobertura || 0);

    if (!codigoCobertura || !freq) return null;

    const poliza = encabezado?.poliza || "";
    const polizaParts = poliza.split("-");
    const ramo = polizaParts.length >= 2 ? Number(polizaParts[1]) : RAMO_HOSPITALIZACION;
    const secuenciaPoliza = polizaParts.length >= 3 ? Number(polizaParts[2]) : 0;

    const fechaActual = new Date().toISOString();
    // Si fechaSalida del encabezado es null, usar la fecha de egreso del formulario
    let fechaSalida = encabezado?.fechaSalida || null;
    if (!fechaSalida && fechaEgresoFormulario) {
      fechaSalida = new Date(`${fechaEgresoFormulario}T00:00:00`).toISOString();
    }
    const fechaNacimiento = encabezado?.fechaNacimiento || null;

    // Si se pasa filaData, usar esos valores; sino usar el draft
    const tipoReclamante = filaData?.tipoAfiliado || draft.tipoAfiliado || encabezado?.tipoCentroMed || "NO_MEDICO";
    const codigoReclamante = filaData?.codigoCentro != null ? Number(filaData.codigoCentro) : Number(draft.codigoCentro || prestadorCodigo || 0);

    // Usar el tipo de servicio del formulario, si no está disponible usar el del encabezado
    const codigoServicio = tipoServicioFormulario 
      ? Number(tipoServicioFormulario) 
      : Number(encabezado?.codigoServicio || 0);

    return {
      ano: Number(anioFormulario),
      compania: Number(empresa?.CODIGO || 0),
      ramo: ramo,
      secuencial: Number(secuencialFormulario),
      secuenciaPoliza: secuenciaPoliza,
      plan: Number(encabezado?.planPoliza || 0),
      servicio: codigoServicio,
      codigoAsegurado: Number(encabezado?.numeroAsegurado || 0),
      tipoAsegurado: encabezado?.tipoAsegurado || "",
      fechaSalida: fechaSalida,
      cobertura: codigoCobertura,
      fechaServicio: fechaActual,
      frecuencia: freq,
      sexo: encabezado?.sexo || "",
      estadoCivil: encabezado?.estadoCivil || "",
      fechaNacimiento: fechaNacimiento,
      tipoReclamante: tipoReclamante,
      reclamante: codigoReclamante,
      montoReclamado: monto,
      checkCirugia: encabezado?.checkCirugia || "N",
      categoriaAsegurado: encabezado?.categoriaAfiliado || "",
      secuenciaAsegurado: Number(encabezado?.secuenciaDependiente || 0),
    };
  };

  const llamarValidacionCobertura = async (cobertura, frecuencia, montoReclamado, filaData = null) => {
    const payload = construirPayloadValidacion(cobertura, frecuencia, montoReclamado, filaData);
    if (!payload || !payload.cobertura || !payload.frecuencia) {
      return;
    }

    // Guardar la fila que se está validando si existe
    filaValidandoRef.current = filaData ? { ...filaData, cobertura: payload.cobertura } : null;
    // Guardar el monto enviado para no sobrescribirlo con el default cuando la validación fue por cambio manual
    montoReclamadoEnviadoRef.current = filaData != null ? montoReclamado : null;
    validarCobertura(payload);
  };

  // Efecto para disparar validación de cobertura después de actualizar el reclamante
  useEffect(() => {
    if (triggerValidacionCobertura && draft.cobertura) {
      llamarValidacionCobertura(
        { cobertura: draft.cobertura },
        draft.frecuencia,
        draft.montoReclamadoCobertura
      );
      setTriggerValidacionCobertura(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [triggerValidacionCobertura]);

  // Ref para rastrear qué ingreso ya fue cargado (solo carga inicial)
  const ingresoCargadoRef = useRef(null);
  // Efecto para cargar coberturas del backend SOLO en carga inicial del ingreso
  useEffect(() => {
    // Crear clave única para identificar el ingreso actual
    const claveIngreso = anioFormulario && secuencialFormulario
      ? `${anioFormulario}-${secuencialFormulario}`
      : null;

    // Si no hay ingreso válido, no hacer nada
    if (!claveIngreso) return;

    // Si ya cargamos este ingreso, no volver a cargar (preserva coberturas en memoria)
    if (ingresoCargadoRef.current === claveIngreso) return;

    const ingresoDetalle = consultarIngresosPssData?.ingresoDetalle;
    const list = Array.isArray(ingresoDetalle) ? ingresoDetalle : [];

    // Solo marcar como cargado si hay datos del backend para cargar
    // o si es la primera vez que vemos este ingreso (para resetear)
    ingresoCargadoRef.current = claveIngreso;

    // Si no hay coberturas del backend, inicializar con array vacío
    if (list.length === 0) {
      setRows([]);
      return;
    }

    // Mapear las coberturas que vienen del backend (solo carga inicial)
    const mappedRows = list.map((d) => {
      const difNoCubierta = Number(d?.montoPagoAfiliado) || 0;
      const mtoCoas = Number(d?.montoCoaseguro) || 0;
      return {
        cobertura: Number(d?.cobertura) || 0,
        descripcionCobertura: d?.descripcionCobertura ?? "",
        tipoAfiliado: d?.tipoCentroMedico || "NO_MEDICO",
        codigoCentro: Number(d?.codigoCentroMedico ?? prestadorCodigo) || 0,
        nombreReclamante: d?.nombreCentro ?? prestadorNombre ?? "",
        limiteCobertura: Number(d?.limiteCobertura) || 0,
        frecuencia: Number(d?.frecuencia) || 1,
        montoReclamadoCobertura: Number(d?.montoReclamadoCobertura) || 0,
        montoPagoCobertura: Number(d?.montoPagoCobertura) || 0,
        montoPagoAfiliado: Number(d?.montoPagoAfiliado) || 0,
        diferenciaNoCubierta: difNoCubierta,
        montoCoaseguro: mtoCoas,
        porcientoCobertura: Number(d?.porcientoCobertura) || 0,
        tipoCobertura: Number(d?.tipoCobertura) || 2,
        reserva: Number(d?.reserva) || 0,
        excedenteCopago: Number(d?.excedenteCopago) || 0,
        estatus: Number(d?.estatus) || 61,
        secuencia: Number(d?.secuencia) || 1, // Guardar secuencia del backend
      };
    });

    setRows(mappedRows);
  }, [consultarIngresosPssData, anioFormulario, secuencialFormulario, prestadorCodigo, prestadorNombre]);

  // Efecto para resetear cuando cambia de ingreso
  useEffect(() => {
    // Si cambia el ingreso (anio o secuencial), resetear el ref para permitir nueva carga
    const claveIngreso = anioFormulario && secuencialFormulario
      ? `${anioFormulario}-${secuencialFormulario}`
      : null;

    // Si la clave es diferente a la que tenemos, resetear
    if (claveIngreso && ingresoCargadoRef.current && ingresoCargadoRef.current !== claveIngreso) {
      ingresoCargadoRef.current = null;
      setRows([]);
      limpiarDraft();
      // Resetear el estado de bloqueo por cobertura
      setFormularioBloqueadoPorCobertura(false);
      limiteCasoAlertMostradaRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anioFormulario, secuencialFormulario]);

  // Efecto para notificar al componente padre cuando cambian las coberturas
  useEffect(() => {
    if (typeof onCoberturasChange === "function") {
      onCoberturasChange(rows);
    }
  }, [rows, onCoberturasChange]);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalPage, setModalPage] = useState(1);
  const [q, setQ] = useState("");
  const [codigoCoberturaBuscar, setCodigoCoberturaBuscar] = useState(null);
  const [isDetalleReclamantesOpen, setIsDetalleReclamantesOpen] = useState(false);
  const validacionTimeoutRef = useRef(null);
  const frecuenciaDebounceRef = useRef(null); // Debounce del onChange de frecuencia (draft)
  const [frecuenciaInputValue, setFrecuenciaInputValue] = useState(""); // Valor mostrado en input mientras se escribe (sync con draft al debounce)
  const [isModalOxigenoOpen, setIsModalOxigenoOpen] = useState(false);
  const [coberturaSeleccionada, setCoberturaSeleccionada] = useState(null);

  const [contratoTipo, setContratoTipo] = useState(TIPO_CONTRATO.LITRO);
  const [cantidadLitros, setCantidadLitros] = useState("");
  const [montoReclamadoOxigeno, setMontoReclamadoOxigeno] = useState("");
  const [filaOxigenoEditando, setFilaOxigenoEditando] = useState(null); // Para saber si estamos editando una fila existente

  // Sincronizar input de frecuencia del draft cuando se selecciona una cobertura o cambia la frecuencia
  useEffect(() => {
    if (draft.cobertura) {
      setFrecuenciaInputValue(String(draft.frecuencia ?? ""));
    }
  }, [draft.cobertura, draft.frecuencia]);

  // Recalcular frecuencia de cobertura 1064 cuando cambia la fecha de egreso
  useEffect(() => {
    if (!fechaEgresoFormulario || !encabezado?.fechaIngreso) {
      return;
    }
    
    const fechaIngresoDate = new Date(encabezado.fechaIngreso);
    const fechaEgresoDate = new Date(`${fechaEgresoFormulario}T00:00:00`);
    
    if (isNaN(fechaIngresoDate.getTime()) || isNaN(fechaEgresoDate.getTime())) {
      return;
    }
    
    const diferenciaMs = fechaEgresoDate - fechaIngresoDate;
    const diferenciaDias = Math.ceil(diferenciaMs / (1000 * 60 * 60 * 24));
    const frecuenciaCalculada = diferenciaDias > 0 ? diferenciaDias : 1;
    
    // Actualizar cobertura 1064 en el draft si está presente
    if (draft.cobertura === COBERTURA_HABITACION && frecuenciaCalculada !== draft.frecuencia) {
      setDraft((prev) => ({
        ...prev,
        frecuencia: frecuenciaCalculada,
        frecuenciaMaxima: frecuenciaCalculada,
        montoReclamadoCobertura: 0,
      }));
    }
    
    // Actualizar cobertura 1064 en las filas de la tabla si está presente
    setRows((prevRows) => {
      const tiene1064 = prevRows.some(r => r.cobertura === COBERTURA_HABITACION);
      if (!tiene1064) {
        return prevRows;
      }
      
      return prevRows.map((r) => {
        if (r.cobertura !== COBERTURA_HABITACION || r.frecuencia === frecuenciaCalculada) {
          return r;
        }
        
        // Actualizar frecuencia y limpiar monto
        const rowActualizada = {
          ...r,
          frecuencia: frecuenciaCalculada,
          frecuenciaMaxima: frecuenciaCalculada,
          montoReclamadoCobertura: 0,
          montoPagoCobertura: 0,
          diferenciaNoCubierta: 0,
          montoCoaseguro: 0,
          montoPagoAfiliado: 0,
          excedenteCopago: 0,
        };
        
        return rowActualizada;
      });
    });
  }, [fechaEgresoFormulario, encabezado?.fechaIngreso]);

  // Al cambiar de ingreso, limpiar estado de la calculadora de oxígeno para no arrastrar datos del flujo anterior
  useEffect(() => {
    const numeroIngreso = encabezado?.numeroIngreso;
    if (!numeroIngreso) return;
    setCantidadLitros("");
    setMontoReclamadoOxigeno("");
    setContratoTipo(TIPO_CONTRATO.LITRO);
    setFilaOxigenoEditando(null);
    setIsModalOxigenoOpen(false);
    limpiarCalcularOxigeno?.();
  }, [encabezado?.numeroIngreso, limpiarCalcularOxigeno]);

  // Actualizar tope de anestesia cuando se modifica el monto de 903/910 en servicios Parto/Cesárea
  useEffect(() => {
    const servicioActual = tipoServicioFormulario;
    
    // Solo para servicios Parto (11) y Cesárea (12)
    if (servicioActual !== SERVICIO_PARTO && servicioActual !== SERVICIO_CESAREA) {
      setTopeAnestesia(null);
      topeAnestesiaAnteriorRef.current = null;
      return;
    }
    
    // Determinar qué cobertura buscar según el servicio
    const coberturaBase = servicioActual === SERVICIO_PARTO ? COBERTURA_PARTO_NORMAL : COBERTURA_CESAREA;
    
    // Buscar la cobertura 903/910 en la tabla
    const cobertura = rows.find(r => Number(r.cobertura) === coberturaBase);
    
    if (cobertura) {
      const montoReclamado = Number(cobertura.montoReclamadoCobertura) || 0;
      const topeAnterior = topeAnestesiaAnteriorRef.current;
      
      // Si el tope cambió (monto de 903/910 cambió), resetear el monto de 1096
      if (topeAnterior != null && topeAnterior !== montoReclamado) {
        const tiene1096 = rows.some(r => Number(r.cobertura) === COBERTURA_ANESTESIA);
        
        if (tiene1096) {
          setRows((prevRows) =>
            prevRows.map((r) => {
              if (Number(r.cobertura) === COBERTURA_ANESTESIA) {
                return {
                  ...r,
                  montoReclamadoCobertura: 0,
                  diferenciaNoCubierta: 0,
                  montoCoaseguro: 0,
                  montoPagoAfiliado: 0,
                  montoPagoCobertura: 0,
                  excedenteCopago: 0,
                };
              }
              return r;
            })
          );
        }
      }
      
      setTopeAnestesia(montoReclamado);
      topeAnestesiaAnteriorRef.current = montoReclamado;
    } else {
      // Si no hay cobertura 903/910, eliminar la 1096 si existe
      const tiene1096 = rows.some(r => Number(r.cobertura) === COBERTURA_ANESTESIA);
      
      if (tiene1096 && topeAnestesiaAnteriorRef.current != null) {
        setRows((prevRows) => prevRows.filter(r => Number(r.cobertura) !== COBERTURA_ANESTESIA));
      }
      
      setTopeAnestesia(null);
      topeAnestesiaAnteriorRef.current = null;
    }
  }, [rows, tipoServicioFormulario]);

  const coberturasFuente = useMemo(() => {
    if (Array.isArray(coberturasDisponibles) && coberturasDisponibles.length > 0)
      return coberturasDisponibles;

    const fromStore = pickCoberturas(consultarCoberturasCierreCuentaData);
    return Array.isArray(fromStore) ? fromStore : [];
  }, [coberturasDisponibles, consultarCoberturasCierreCuentaData]);

  const totalRegistros = useMemo(() => {
    return pickTotalRegistros(consultarCoberturasCierreCuentaData);
  }, [consultarCoberturasCierreCuentaData]);

  // Efecto para controlar el estado de carga de coberturas
  useEffect(() => {
    // Usar el prop de loading directamente si está disponible
    if (consultarCoberturasCierreCuentaLoading !== undefined) {
      setCoberturasCargando(consultarCoberturasCierreCuentaLoading);
    } else {
      // Fallback: Si hay un ingreso seleccionado pero no hay coberturas cargadas aún, marcar como cargando
      const hayIngreso = encabezado && anioFormulario && secuencialFormulario;
      const hayCoberturas = coberturasFuente.length > 0;
      
      if (hayIngreso && !hayCoberturas && !consultarCoberturasCierreCuentaData) {
        setCoberturasCargando(true);
      } else if (consultarCoberturasCierreCuentaData !== undefined && consultarCoberturasCierreCuentaData !== null) {
        setCoberturasCargando(false);
      }
    }
  }, [consultarCoberturasCierreCuentaLoading, consultarCoberturasCierreCuentaData, coberturasFuente, encabezado, anioFormulario, secuencialFormulario]);

  // Filtrar coberturas: si el servicio es ambulatorio, excluir cobertura 1064 (Habitación)
  const pagedCoberturas = useMemo(() => {
    if (tipoServicioFormulario === SERVICIO_AMBULATORIO) {
      return coberturasFuente.filter((cob) => Number(cob.cobertura) !== COBERTURA_HABITACION);
    }
    return coberturasFuente;
  }, [coberturasFuente, tipoServicioFormulario]);

  const shouldOpenModalRef = useRef(false);

  const openModal = () => {
    if (isDisabled) {
      if (!tieneFechaEgreso) {
        setAlertConfig({
          type: "info",
          title: "¡Atención!",
          message: "Debe establecer la fecha de egreso antes de agregar coberturas.",
          onConfirm: () => setShowAlert(false),
        });
        setShowAlert(true);
      }
      return;
    }

    shouldOpenModalRef.current = true;
    setModalPage(1);
    setQ("");
    setCodigoCoberturaBuscar(null);
    
    // Consultar coberturas al abrir el modal
    if (typeof onConsultarCoberturas === "function") {
      onConsultarCoberturas(tipoServicioFormulario, 1, null);
    }
  };

  useEffect(() => {
    if (!shouldOpenModalRef.current) return;

    const tieneCoberturas = Array.isArray(coberturasFuente) && coberturasFuente.length > 0;
    const cargaCompleta = 
      !consultarCoberturasCierreCuentaLoading && 
      consultarCoberturasCierreCuentaData !== undefined && 
      consultarCoberturasCierreCuentaData !== null;

    if (tieneCoberturas || cargaCompleta) {
      setIsModalOpen(true);
      shouldOpenModalRef.current = false;
    }
  }, [coberturasFuente, consultarCoberturasCierreCuentaLoading, consultarCoberturasCierreCuentaData]);

  const closeModal = () => {
    setIsModalOpen(false);
    shouldOpenModalRef.current = false;
  };

  // Cuando cambia el campo de búsqueda, extraer el código de cobertura
  useEffect(() => {
    const codigoCobertura = q.trim();
    const coberturaParam = codigoCobertura && !isNaN(Number(codigoCobertura)) 
      ? Number(codigoCobertura) 
      : null;
    
    // Validar ANTES de buscar: si es cobertura 1064 y servicio ambulatorio, bloquear
    if (coberturaParam === COBERTURA_HABITACION && tipoServicioFormulario === SERVICIO_AMBULATORIO) {
      setAlertConfig({
        type: "error",
        title: "¡Error!",
        message: "No se puede agregar la cobertura de habitación al ingreso ambulatorio.",
        onConfirm: () => setShowAlert(false),
      });
      setShowAlert(true);
      setQ(""); // Limpiar el campo de búsqueda
      return;
    }
    
    if (codigoCoberturaBuscar !== coberturaParam) {
      setCodigoCoberturaBuscar(coberturaParam);
      setModalPage(1); // Resetear a página 1 cuando cambia la búsqueda
    }
  }, [q, codigoCoberturaBuscar, tipoServicioFormulario]);

  // Cuando cambia la página o el código de cobertura, consultar al backend
  useEffect(() => {
    if (!isModalOpen) return;
    
    if (typeof onConsultarCoberturas === "function") {
      onConsultarCoberturas(tipoServicioFormulario, modalPage, codigoCoberturaBuscar);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalPage, codigoCoberturaBuscar, isModalOpen]);

  const onSelectCobertura = (c, oxigenoCalculado = false, montoOxigeno = 0) => {
    setCoberturaSeleccionada(c);

    const tipoAfiliadoActual = draft.tipoAfiliado || "NO_MEDICO";
    const codigoCobertura = Number(c?.cobertura) || 0;
    const codigoReclamanteActual = draft.codigoCentro || prestadorCodigo || 0;

    // Validar si la cobertura ya existe en la tabla
    if (validarCoberturaExistente(codigoCobertura)) {
      setIsModalOpen(false);
      shouldOpenModalRef.current = false;
      setAlertConfig({
        type: "error",
        title: "¡Error!",
        message: "Esta cobertura ya existe en la tabla. No se pueden agregar coberturas duplicadas.",
        onConfirm: () => setShowAlert(false),
      });
      setShowAlert(true);
      return;
    }

    // Validar si es reclamante médico y ya existe la misma cobertura con el mismo código de reclamante
    if (validarCoberturaDuplicada(codigoCobertura, tipoAfiliadoActual, codigoReclamanteActual)) {
      setIsModalOpen(false);
      shouldOpenModalRef.current = false;
      setAlertConfig({
        type: "error",
        title: "¡Error!",
        message: "No se puede agregar la misma cobertura para un reclamante médico que ya existe en la tabla.",
        onConfirm: () => setShowAlert(false),
      });
      setShowAlert(true);
      return;
    }

    // Validar que no se pueda agregar cobertura de Habitación (1064) en servicio Ambulatorio
    if (codigoCobertura === COBERTURA_HABITACION && tipoServicioFormulario === SERVICIO_AMBULATORIO) {
      setIsModalOpen(false);
      shouldOpenModalRef.current = false;
      setAlertConfig({
        type: "error",
        title: "¡Error!",
        message: "No se puede agregar la cobertura de habitación al ingreso ambulatorio.",
        onConfirm: () => setShowAlert(false),
      });
      setShowAlert(true);
      return;
    }

    // Habitación conserva su flujo; el resto siempre inicia en frecuencia 1
    const nuevaFrecuencia = codigoCobertura === COBERTURA_HABITACION ? (draft.frecuencia || 1) : 1;

    // Si es oxígeno y no está calculado, actualizar draft y llamar validación antes de abrir el popup
    if(codigoCobertura === COBERTURA_OXIGENO && !oxigenoCalculado){
      setDraft((prev) => ({
        ...prev,
        cobertura: codigoCobertura,
        descripcionCobertura: c?.descripcionCobertura ?? "",
        tipoCobertura: c?.tipoCobertura != null ? Number(c.tipoCobertura) : 2,
        frecuencia: 1,
        frecuenciaMaxima: null,
        limiteCobertura: c?.limiteCobertura || prev.limiteCobertura || 0,
        // Mantener el código reclamante y nombre si el usuario ya los ingresó
        codigoCentro: prev.codigoCentro || Number(prestadorCodigo) || 0,
        nombreReclamante: prev.nombreReclamante || prestadorNombre || "",
      }));

      setIsModalOpen(false);
      shouldOpenModalRef.current = false;

      // Llamar a validación antes de abrir el popup de oxígeno
      // El monto será 0 porque aún no se ha calculado en el popup
      if (codigoCobertura && nuevaFrecuencia) {
        llamarValidacionCobertura(c, nuevaFrecuencia, 0);
      }

      // Limpiar estado de flujos anteriores antes de abrir el modal
      setCantidadLitros("");
      setMontoReclamadoOxigeno("");
      setContratoTipo(TIPO_CONTRATO.LITRO);
      setFilaOxigenoEditando(null);
      limpiarCalcularOxigeno?.();

      // Abrir el popup de oxígeno después de llamar la validación
      setIsModalOxigenoOpen(true);
      return;
    }

    // Otra cobertura con monto abierto (no oxígeno): validar solo al inicio con monto 0, monto reclamado editable después
    if (esCoberturaMontoAbierto(codigoCobertura) && codigoCobertura !== COBERTURA_OXIGENO) {
      setDraft((prev) => ({
        ...prev,
        cobertura: codigoCobertura,
        descripcionCobertura: c?.descripcionCobertura ?? "",
        tipoCobertura: c?.tipoCobertura != null ? Number(c.tipoCobertura) : 2,
        frecuencia: 1,
        frecuenciaMaxima: null,
        montoReclamadoCobertura: 0,
        limiteCobertura: c?.limiteCobertura || prev.limiteCobertura || 0,
        codigoCentro: prev.codigoCentro || Number(prestadorCodigo) || 0,
        nombreReclamante: prev.nombreReclamante || prestadorNombre || "",
      }));

      setIsModalOpen(false);
      shouldOpenModalRef.current = false;

      if (codigoCobertura) {
        llamarValidacionCobertura(c, 1, 0);
      }
      return;
    }

    const nuevoMonto = codigoCobertura === COBERTURA_OXIGENO ? montoOxigeno : draft.montoReclamadoCobertura || 0;

    // Si es cobertura con monto abierto (oxígeno ya calculado u otra con %) calcular localmente
    const porcientoCobertura = c?.porcientoCobertura !== undefined ? c.porcientoCobertura : draft.porcientoCobertura;
    const tienePorcientoCobertura = porcientoCobertura !== undefined && porcientoCobertura !== null;
    const esMontoAbiertoConPorciento = esCoberturaMontoAbierto(codigoCobertura) && (codigoCobertura === COBERTURA_OXIGENO ? oxigenoCalculado : true) && tienePorcientoCobertura;

    if (esMontoAbiertoConPorciento) {
      const porcientoCoaseguro = Number(porcientoCobertura);
      // Calcular distribución considerando límite por caso
      const totalCuentaOtrasSelect = rows.reduce((acc, r) => acc + (Number(r.montoReclamadoCobertura) || 0), 0);
      const limiteCasoSelect = Number(encabezado?.limiteCaso) || 0;
      const distribucionSelect = calcularDistribucionConLimiteCaso(nuevoMonto, porcientoCoaseguro, totalCuentaOtrasSelect, limiteCasoSelect);

      setDraft((prev) => ({
        ...prev,
        cobertura: codigoCobertura,
        descripcionCobertura: c?.descripcionCobertura ?? "",
        tipoCobertura: c?.tipoCobertura != null ? Number(c.tipoCobertura) : 2,
        frecuencia: 1,
        frecuenciaMaxima: null,
        montoReclamadoCobertura: nuevoMonto,
        diferenciaNoCubierta: distribucionSelect.diferenciaNoCubierta,
        montoCoaseguro: distribucionSelect.diferenciaNoCubierta,
        montoPagoAfiliado: distribucionSelect.diferenciaNoCubierta,
        montoPagoCobertura: distribucionSelect.montoPagoCobertura,
        excedenteCopago: distribucionSelect.diferenciaNoCubierta,
        porcientoCobertura: porcientoCoaseguro,
        codigoCentro: prev.codigoCentro || Number(prestadorCodigo) || 0,
        nombreReclamante: prev.nombreReclamante || prestadorNombre || "",
      }));

      // Para coberturas de monto abierto con monto asignado, validar límite por caso
      if (nuevoMonto > 0) {
        const totalConMonto = totalCuentaOtrasSelect + nuevoMonto;
        validarLimitePorCaso(totalConMonto);
      }
    } else {
      // Cobertura 1064 (Habitación) inicia con monto 0, no calcula automáticamente
      const montoInicial = codigoCobertura === COBERTURA_HABITACION ? 0 : nuevoMonto;
      
      setDraft((prev) => ({
        ...prev,
        cobertura: codigoCobertura,
        descripcionCobertura: c?.descripcionCobertura ?? "",
        tipoCobertura: c?.tipoCobertura != null ? Number(c.tipoCobertura) : 2,
        frecuencia: nuevaFrecuencia,
        frecuenciaMaxima: null,
        montoReclamadoCobertura: montoInicial,
        codigoCentro: prev.codigoCentro || Number(prestadorCodigo) || 0,
        nombreReclamante: prev.nombreReclamante || prestadorNombre || "",
      }));

      if (codigoCobertura && nuevaFrecuencia) {
        setTimeout(() => {
          llamarValidacionCobertura(c, nuevaFrecuencia, montoInicial);
        }, 100);
      }
      
      // Para cobertura 1064, calcular frecuencia DESPUÉS de llamar validarCobertura
      if (codigoCobertura === COBERTURA_HABITACION) {
        const fechaIngreso = encabezado?.fechaIngreso;
        if (fechaIngreso && fechaEgresoFormulario) {
          const fechaIngresoDate = new Date(fechaIngreso);
          const fechaEgresoDate = new Date(`${fechaEgresoFormulario}T00:00:00`);
          
          if (!isNaN(fechaIngresoDate.getTime()) && !isNaN(fechaEgresoDate.getTime())) {
            const diferenciaMs = fechaEgresoDate - fechaIngresoDate;
            const diferenciaDias = Math.ceil(diferenciaMs / (1000 * 60 * 60 * 24));
            const frecuenciaCalculada = diferenciaDias > 0 ? diferenciaDias : 1;
            
            setDraft((prev) => ({
              ...prev,
              frecuencia: frecuenciaCalculada,
              frecuenciaMaxima: frecuenciaCalculada,
            }));
          }
        }
      }
    }

    setIsModalOpen(false);
    shouldOpenModalRef.current = false;
  };

  const construirPayloadCompleto = () => {
    if (!encabezado || !anioFormulario || !secuencialFormulario || !draft.cobertura) {
      return null;
    }

    const fechaActual = new Date().toISOString();

    return {
      ano: draft.ano,
      secuencial: draft.secuencial,
      tipoCobertura: draft.tipoCobertura,
      cobertura: draft.cobertura,
      frecuencia: draft.frecuencia,
      montoReclamadoCobertura: draft.montoReclamadoCobertura,
      ramoIngreso: draft.ramoIngreso,
      codigoCompania: draft.codigoCompania,
      codigoCentro: draft.codigoCentro,
      limiteCobertura: draft.limiteCobertura,
      porcientoCobertura: draft.porcientoCobertura,
      montoPagoCobertura: draft.montoPagoCobertura,
      montoPagoAfiliado: draft.montoPagoAfiliado,
      excedenteCopago: draft.excedenteCopago,
      servicio: draft.servicio,
      fechaServicio: fechaActual,
      secuencia: draft.secuencia,
      reserva: draft.reserva,
      estatus: draft.estatus,
      tipoAfiliado: draft.tipoAfiliado,
      usuarioTransaccion: draft.usuarioTransaccion,
      fechaTransaccion: fechaActual,
      fechaIngresoCobertura: fechaActual,
      usuarioIngresoCobertura: draft.usuarioIngresoCobertura,
    };
  };

  const validarCoberturaDuplicada = (cobertura, tipoAfiliado, codigoReclamante) => {
    // Solo validar si es reclamante médico
    if (tipoAfiliado !== "MEDICO") {
      return false;
    }

    // Verificar en la tabla actual (rows)
    const existeEnTabla = rows.some(
      (row) =>
        row.tipoAfiliado === "MEDICO" &&
        Number(row.cobertura) === Number(cobertura) &&
        Number(row.codigoCentro) === Number(codigoReclamante)
    );

    if (existeEnTabla) return true;

    // Verificar en el detalle de ingreso original (backend)
    const ingresoDetalle = consultarIngresosPssData?.ingresoDetalle || [];
    const existeEnDetalle = ingresoDetalle.some(
      (det) =>
        (det.tipoCentroMedico || "NO_MEDICO") === "MEDICO" &&
        Number(det.cobertura) === Number(cobertura) &&
        Number(det.codigoCentroMedico || 0) === Number(codigoReclamante)
    );

    return existeEnDetalle;
  };

  // Validar si una cobertura ya existe en la tabla (sin importar el tipo de reclamante)
  const validarCoberturaExistente = (codigoCobertura) => {
    // Verificar en la tabla actual (rows)
    const existeEnTabla = rows.some((row) => Number(row.cobertura) === Number(codigoCobertura));
    
    if (existeEnTabla) return true;

    // Verificar en el detalle de ingreso original (backend)
    const ingresoDetalle = consultarIngresosPssData?.ingresoDetalle || [];
    const existeEnDetalle = ingresoDetalle.some(
      (det) => Number(det.cobertura) === Number(codigoCobertura)
    );

    return existeEnDetalle;
  };

  // Función para validar límite por caso (informativa, no bloquea, se muestra una sola vez)
  const validarLimitePorCaso = (nuevoTotalCuenta = null) => {
    const limiteCaso = Number(encabezado?.limiteCaso) || 0;

    // Si ya se mostró la alerta, no volver a mostrarla
    if (limiteCasoAlertMostradaRef.current) return false;

    // Si no se proporciona un total, calcularlo
    let totalCuenta = nuevoTotalCuenta;
    if (totalCuenta === null) {
      totalCuenta = rows.reduce((acc, r) => acc + (Number(r.montoReclamadoCobertura) || 0), 0);
      if (draft.cobertura) {
        totalCuenta += Number(draft.montoReclamadoCobertura) || 0;
      }
    }

    if (totalCuenta > limiteCaso) {
      const servicioIngreso = String(tipoServicioFormulario || encabezado?.codigoServicio || "");
      const esServicioMaternidad =
        servicioIngreso === SERVICIO_MATERNIDAD ||
        servicioIngreso === SERVICIO_PARTO ||
        servicioIngreso === SERVICIO_CESAREA;

      limiteCasoAlertMostradaRef.current = true;
      setAlertConfig({
        type: "warning",
        title: "¡Advertencia - Límite por caso!",
        message: esServicioMaternidad
          ? "Se aplicó el monto disponible de maternidad según el plan. El excedente se refleja como Diferencia del Asegurado."
          : "Se autorizó hasta el límite por caso disponible y el excedente se reflejó como Diferencia del Asegurado.",
        onConfirm: () => setShowAlert(false),
      });
      setShowAlert(true);
      return false;
    }

    return true;
  };

  // Función para validar el tope de la cobertura 1096 (Anestesia)
  const validarTopeCobertura1096 = (montoNuevo1096, codigoCobertura, secuencia = null, actualizarMonto = null) => {
    // Solo validar si es la cobertura 1096
    if (Number(codigoCobertura) !== COBERTURA_ANESTESIA) return true;

    const servicioActual = tipoServicioFormulario;
    let topePrecertificado = 0;
    let mensajeDetalle = "";
    
    // Para servicios Parto (11) y Cesárea (12)
    if (servicioActual === SERVICIO_PARTO || servicioActual === SERVICIO_CESAREA) {
      // Si no hay tope seteado, mostrar error indicando que deben cargar 903/910 primero
      if (topeAnestesia == null || topeAnestesia <= 0) {
        const coberturaRequerida = servicioActual === SERVICIO_PARTO ? "910 (Parto Normal)" : "903 (Operación Cesárea)";
        setAlertConfig({
          type: "error",
          title: "¡Cobertura requerida!",
          message: `Para agregar la cobertura de Anestesia (1096) en este servicio, primero debe agregar la cobertura ${coberturaRequerida} y establecer su monto reclamado.`,
          onConfirm: () => setShowAlert(false),
        });
        setShowAlert(true);
        return false;
      }
      
      topePrecertificado = topeAnestesia;
      mensajeDetalle = servicioActual === SERVICIO_PARTO
        ? "El Monto de Anestesia (1096) no puede superar el monto de la cobertura de Parto Normal (910)."
        : "El Monto de Anestesia (1096) no puede superar el monto de la cobertura de Operación Cesárea (903).";
    } else {
      // Para otros servicios, calcular el tope: suma de montos reclamados de coberturas precertificadas (esNueva !== true)
      topePrecertificado = rows
        .filter((r) => r.esNueva !== true)
        .reduce((acc, r) => acc + (Number(r.montoReclamadoCobertura) || 0), 0);
      
      // Si no hay tope, no validar
      if (topePrecertificado <= 0) return true;
      
      mensajeDetalle = "El Monto de Anestesia (1096) no puede superar el total de las coberturas precertificadas.";
    }

    // Calcular el total actual de la 1096 (excluyendo la fila que se está editando)
    const total1096Actual = rows
      .filter((r) => Number(r.cobertura) === COBERTURA_ANESTESIA && r.secuencia !== secuencia)
      .reduce((acc, r) => acc + (Number(r.montoReclamadoCobertura) || 0), 0);

    // Sumar el nuevo monto
    const total1096Nuevo = total1096Actual + montoNuevo1096;

    // Validar si supera el tope
    if (total1096Nuevo > topePrecertificado) {
      const montoMaximoPermitido = topePrecertificado - total1096Actual;
      const excedente = total1096Nuevo - topePrecertificado;
      
      // Si se proporciona callback para actualizar, ajustar automáticamente al tope
      if (actualizarMonto && typeof actualizarMonto === 'function') {
        actualizarMonto(montoMaximoPermitido > 0 ? montoMaximoPermitido : 0);
      }
      
      setAlertConfig({
        type: "warning",
        title: "¡Monto ajustado automáticamente!",
        message: `${mensajeDetalle}\n\nTope permitido: ${topePrecertificado.toLocaleString("es-DO", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}\nMonto ingresado: ${total1096Nuevo.toLocaleString("es-DO", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}\nExcedente: ${excedente.toLocaleString("es-DO", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}\n\nEl monto se ha ajustado automáticamente a: ${(montoMaximoPermitido > 0 ? montoMaximoPermitido : 0).toLocaleString("es-DO", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
        onConfirm: () => setShowAlert(false),
      });
      setShowAlert(true);
      return false;
    }

    return true;
  };

  // Nueva función: manejar validación de código digitado con debounce
  const validarCodigoDigitadoDebounced = (codigoCobertura) => {
    // Limpiar timer anterior
    if (debounceCodigoCoberturaRef.current) {
      clearTimeout(debounceCodigoCoberturaRef.current);
    }

    // Si está vacío o es 0, no hacer nada
    if (!codigoCobertura || codigoCobertura === 0) {
      setCodigoCoberturaBuscando(null);
      return;
    }

    // Validar ANTES de buscar: si es cobertura 1064 y servicio ambulatorio, bloquear
    if (Number(codigoCobertura) === COBERTURA_HABITACION && tipoServicioFormulario === SERVICIO_AMBULATORIO) {
      // Limpiar el estado de carga ANTES de mostrar el error
      setCodigoCoberturaBuscando(null);
      
      setAlertConfig({
        type: "error",
        title: "¡Error!",
        message: "No se puede agregar la cobertura de habitación al ingreso ambulatorio.",
        onConfirm: () => {
          setShowAlert(false);
          // Limpiar el campo de código de cobertura
          setDraft((prev) => ({ ...prev, cobertura: 0, descripcionCobertura: "" }));
        },
      });
      setShowAlert(true);
      return;
    }

    // Marcar que se está buscando
    setCodigoCoberturaBuscando(codigoCobertura);

    // Esperar 800ms después de que el usuario deje de escribir
    debounceCodigoCoberturaRef.current = setTimeout(async () => {
      try {
        // Llamar al servicio directamente con el código de cobertura
        const encabezado = consultarIngresosPssData?.ingresoEncabezado;
        if (!encabezado) {
          setCodigoCoberturaBuscando(null);
          return;
        }

        const codigoServicio = tipoServicioFormulario 
          ? Number(tipoServicioFormulario) 
          : Number(encabezado.codigoServicio || 0);

        const payload = {
          Compania: 30,
          Reclamante: Number(prestadorCodigo),
          Servicio: codigoServicio,
          TipoReclamante: authUserData?.prestador?.tipoPss || "NO_MEDICO",
          Plan: encabezado.planPoliza,
          tamanoPagina: 10,
          numeroPagina: 1,
          cobertura: Number(codigoCobertura),
        };

        // Importar el servicio y llamarlo directamente
        const EgresosService = require("../../../services/egresos/EgresosService").default;
        const response = await EgresosService.consultarCoberturasCierreCuenta(payload);
        
        setCodigoCoberturaBuscando(null);

        // Tomar el primer resultado
        const coberturas = response?.data?.coberturasCierresCuentas || response?.coberturasCierresCuentas || [];
        
        if (coberturas.length > 0) {
          const coberturaEncontrada = coberturas[0];
          // Autoseleccionar la cobertura encontrada
          onSelectCobertura(coberturaEncontrada);
        } else {
          // No se encontró la cobertura
          const servicioDescripcion = encabezado?.descripcionServicio || "el servicio seleccionado";
          
          setAlertConfig({
            type: "confirm",
            title: "¡Advertencia!",
            message: `La cobertura correspondiente al PSS no se encuentra contratada para el servicio ${servicioDescripcion} o no puede ser liberada por esta vía.\n\n¿Está seguro de que desea continuar sin la cobertura registrada?`,
            confirmText: "Sí",
            cancelText: "No",
            onConfirm: () => {
              setShowAlert(false);
              setDraft((p) => ({
                ...p,
                cobertura: 0,
                descripcionCobertura: "",
              }));
            },
            onCancel: () => {
              setShowAlert(false);
              setDraft((p) => ({
                ...p,
                cobertura: 0,
                descripcionCobertura: "",
              }));
              setTimeout(() => {
                setAlertConfig({
                  type: "info",
                  title: "Información",
                  message: "Para validar y concluir el egreso, favor de contactar a nuestro centro de atención al cliente llamando al 809-476-3535, opción 1, luego opción 1.",
                  onConfirm: () => {
                    setShowAlert(false);
                    setFormularioBloqueadoPorCobertura(true);
                  },
                });
                setShowAlert(true);
              }, 100);
            },
          });
          setShowAlert(true);
        }
      } catch (error) {
        console.error("Error al buscar cobertura:", error);
        setCodigoCoberturaBuscando(null);
        
        setAlertConfig({
          type: "error",
          title: "Error",
          message: "Error al buscar la cobertura. Por favor, intente de nuevo.",
          onConfirm: () => {
            setShowAlert(false);
            setDraft((prev) => ({ ...prev, cobertura: 0, descripcionCobertura: "" }));
          },
        });
        setShowAlert(true);
      }
    }, 800);
  };

  // Función para calcular la siguiente secuencia (autoincremental)
  const calcularSiguienteSecuencia = () => {
    if (rows.length === 0) return 1;
    const maxSecuencia = Math.max(...rows.map((r) => Number(r.secuencia) || 0));
    return maxSecuencia + 1;
  };

  const addDraftRow = () => {
    if (isDisabled || !draft.cobertura) return;

    // Validar que el monto reclamado sea mayor a 0
    const montoReclamado = Number(draft.montoReclamadoCobertura) || 0;
    if (montoReclamado <= 0) {
      setErrorMontoReclamado(true);
      return;
    }

    // Validar si la cobertura ya existe en la tabla
    if (validarCoberturaExistente(draft.cobertura)) {
      setAlertConfig({
        type: "error",
        title: "¡Error!",
        message: "Esta cobertura ya existe en la tabla. No se pueden agregar coberturas duplicadas.",
        onConfirm: () => setShowAlert(false),
      });
      setShowAlert(true);
      return;
    }

    // Validar si es reclamante médico y ya existe la misma cobertura con el mismo código de reclamante
    if (validarCoberturaDuplicada(draft.cobertura, draft.tipoAfiliado, draft.codigoCentro)) {
      setAlertConfig({
        type: "error",
        title: "¡Error!",
        message: "No se puede agregar la misma cobertura para un reclamante médico que ya existe en la tabla.",
        onConfirm: () => setShowAlert(false),
      });
      setShowAlert(true);
      return;
    }

    // Validaciones adicionales en memoria antes de agregar
    const frecuenciaActual = Number(draft.frecuencia) || 1;
    if (draft.frecuenciaMaxima != null && frecuenciaActual > Number(draft.frecuenciaMaxima)) {
      setAlertConfig({
        type: "error",
        title: "¡Error!",
        message: `La frecuencia no puede ser mayor a ${draft.frecuenciaMaxima}.`,
        onConfirm: () => setShowAlert(false),
      });
      setShowAlert(true);
      return;
    }

    const limiteContrato = Number(draft.limiteCobertura) || 0;
    const montoMaximo = limiteContrato * frecuenciaActual;
    const esMontoAbiertoDraft = esCoberturaMontoAbierto(draft.cobertura);
    if (!esMontoAbiertoDraft && montoMaximo > 0) {
      const montoActual = Number(draft.montoReclamadoCobertura) || 0;
      if (montoActual > montoMaximo) {
        setAlertConfig({
          type: "info",
          title: "¡Atención!",
          message: `El monto reclamado no puede ser mayor a ${montoMaximo.toLocaleString("es-DO")} (Frecuencia × Límite Contrato).`,
          onConfirm: () => setShowAlert(false),
        });
        setShowAlert(true);
        return;
      }
    }

    // Agregar directamente usando el draft actual (ya validado al seleccionar/cambiar reclamante)
    agregarFilaATabla();
  };

  // Función auxiliar para agregar la fila después de validar
  const agregarFilaATabla = (datosValidados = null) => {
    const payloadCompleto = construirPayloadCompleto();
    const nuevaSecuencia = calcularSiguienteSecuencia();

    // Si se pasan datos validados, usar esos; sino usar el draft actual
    const filaParaAgregar = datosValidados 
      ? { ...draft, ...datosValidados }
      : { ...draft };

    // Validar tope de cobertura 1096 antes de agregar la fila
    const montoAgregar = Number(filaParaAgregar.montoReclamadoCobertura) || 0;
    if (!validarTopeCobertura1096(montoAgregar, filaParaAgregar.cobertura, null)) {
      return; // No agregar la fila si supera el tope
    }

    setRows((prev) => [
      ...prev,
      {
        ...filaParaAgregar,
        secuencia: nuevaSecuencia,
        payloadCompleto,
        esNueva: true, // Marcar como nueva (solo en memoria, no viene del backend)
      },
    ]);

    const fechaActual = new Date().toISOString();
    setDraft((prev) => ({
      ...prev,
      cobertura: 0,
      descripcionCobertura: "",
      tipoCobertura: 2,
      frecuencia: 1,
      frecuenciaMaxima: null, // Resetear frecuencia máxima
      montoReclamadoCobertura: 0,
      limiteCobertura: 0,
      porcientoCobertura: 0,
      montoPagoCobertura: 0,
      montoPagoAfiliado: 0,
      diferenciaNoCubierta: 0,
      montoCoaseguro: 0,
      excedenteCopago: 0,
      reserva: 0,
      fechaServicio: fechaActual,
      fechaTransaccion: fechaActual,
      fechaIngresoCobertura: fechaActual,
      // Siempre default del PSS no médico logueado al abrir nuevo draft
      tipoAfiliado: tipoPss || "NO_MEDICO",
      codigoCentro: Number(prestadorCodigo) || 0,
      nombreReclamante: prestadorNombre || "",
    }));

    setFrecuenciaInputValue("");
    if (frecuenciaDebounceRef.current) {
      clearTimeout(frecuenciaDebounceRef.current);
      frecuenciaDebounceRef.current = null;
    }
  };

  const removeRow = (row) => {
    if (isDisabled) return;

    const codigoCobertura = Number(row.cobertura) || 0;
    if (!codigoCobertura) return;

    // Si tiene los datos necesarios para eliminar en el backend
    if (anioFormulario && secuencialFormulario) {
      const payload = {
        ano: Number(anioFormulario) || 0,
        compania: Number(empresa?.CODIGO) || 0,
        ramo: RAMO_HOSPITALIZACION,
        secuencial: Number(secuencialFormulario) || 0,
        cobertura: codigoCobertura,
      };

      // Guardar el código de cobertura que se está eliminando
      coberturaEliminandoRef.current = codigoCobertura;
      eliminarCobertura(payload);
    } else {
      // Si no tiene los datos necesarios, eliminar directamente del estado local
      setRows((prev) => prev.filter((r) => Number(r.cobertura) !== codigoCobertura));
    }
  };

  // Función para obtener reclamantes únicos de la tabla según el tipo
  const obtenerReclamantesUnicos = (tipoReclamante) => {
    const reclamantesMap = new Map();
    
    rows.forEach((row) => {
      if (row.tipoAfiliado === tipoReclamante) {
        const key = `${row.codigoCentro}`;
        if (!reclamantesMap.has(key)) {
          reclamantesMap.set(key, {
            codigo: row.codigoCentro,
            nombre: row.nombreReclamante,
            tipo: row.tipoAfiliado,
          });
        }
      }
    });
    
    return Array.from(reclamantesMap.values());
  };

  // Manejadores de click para botones de voucher
  const handleVoucherTermico = () => {
    const reclamantes = obtenerReclamantesUnicos("NO_MEDICO");
    setVoucherConfig({
      tipo: 'termico',
      reclamantes,
      reclamanteSeleccionado: reclamantes.length === 1 ? reclamantes[0].codigo : null,
      esMatricial: false,
    });
    setShowVoucherModal(true);
  };

  const handleVoucherMatricial = () => {
    const reclamantes = obtenerReclamantesUnicos("NO_MEDICO");
    setVoucherConfig({
      tipo: 'matricial',
      reclamantes,
      reclamanteSeleccionado: reclamantes.length === 1 ? reclamantes[0].codigo : null,
      esMatricial: true,
    });
    setShowVoucherModal(true);
  };

  const handleVoucherMedico = () => {
    const reclamantes = obtenerReclamantesUnicos("MEDICO");
    setVoucherConfig({
      tipo: 'medico',
      reclamantes,
      reclamanteSeleccionado: reclamantes.length === 1 ? reclamantes[0].codigo : null,
      esMatricial: false, // Por defecto térmico, el usuario puede cambiar
    });
    setShowVoucherModal(true);
  };

  // Generar voucher
  const generarVoucher = async () => {
    if (!voucherConfig.reclamanteSeleccionado) {
      setAlertConfig({
        type: "warning",
        title: "Advertencia",
        message: "Debe seleccionar un reclamante.",
        onConfirm: () => setShowAlert(false),
      });
      setShowAlert(true);
      return;
    }

    if (!encabezado || !encabezado.numeroIngreso) {
      setAlertConfig({
        type: "error",
        title: "Error",
        message: "No se puede generar el voucher. Información de ingreso no disponible.",
        onConfirm: () => setShowAlert(false),
      });
      setShowAlert(true);
      return;
    }

    setDescargandoVoucher(true);

    try {
      // Extraer año y secuencial del numeroIngreso (formato: "2026-9661450")
      const partes = encabezado.numeroIngreso.split("-");
      const ano = partes[0];
      const secuencial = partes[1];

      // Extraer ramo de la poliza
      const poliza = encabezado?.poliza || "";
      const polizaParts = poliza.split("-");
      const ramo = polizaParts.length >= 2 ? Number(polizaParts[1]) : 95;

      const tipoReclamante = voucherConfig.tipo === 'medico' ? 'MEDICO' : 'NO_MEDICO';

      const payload = {
        Ano: Number(ano),
        Compania: 30,
        Ramo: ramo,
        Secuencial: Number(secuencial),
        CodigoPss: Number(voucherConfig.reclamanteSeleccionado),
        TipoReclamante: tipoReclamante,
        EsMatricial: voucherConfig.esMatricial,
      };

      const EgresosService = require("../../../services/egresos/EgresosService").default;
      await EgresosService.descargarVoucherEgreso(payload);

      setShowVoucherModal(false);
      setAlertConfig({
        type: "success",
        title: "Éxito",
        message: "Voucher descargado exitosamente.",
        onConfirm: () => setShowAlert(false),
      });
      setShowAlert(true);
    } catch (error) {
      console.error("Error al descargar voucher:", error);
      setAlertConfig({
        type: "error",
        title: "Error",
        message: error?.mensaje || "Error al descargar el voucher.",
        onConfirm: () => setShowAlert(false),
      });
      setShowAlert(true);
    } finally {
      setDescargandoVoucher(false);
    }
  };

  const handleMontoReclamadoChange = (codigoCobertura, nuevoValor, secuencia, row = null) => {
    if (!esVigente) return;
    
    // Permitir campo vacío
    if (nuevoValor === "") {
      setRows((prev) =>
        prev.map((r) =>
          Number(r.cobertura) === Number(codigoCobertura) && r.secuencia === secuencia
            ? { ...r, montoReclamadoCobertura: "" }
            : r
        )
      );
      return;
    }
    
    // Validación simple: solo números, punto decimal, máximo 10 dígitos antes del punto y 2 después
    const tieneMultiplesPuntos = (nuevoValor.match(/\./g) || []).length > 1;
    const partesDecimal = nuevoValor.split(".");
    const tieneDemasiadosDecimales = partesDecimal[1] && partesDecimal[1].length > 2;
    const tieneDemasiadosEnteros = partesDecimal[0] && partesDecimal[0].length > 10;
    const tieneCaracteresInvalidos = /[^\d.]/.test(nuevoValor);
    
    if (tieneCaracteresInvalidos || tieneMultiplesPuntos || tieneDemasiadosDecimales || tieneDemasiadosEnteros) {
      return;
    }

    const valorIngresado = Number(nuevoValor) || 0;
    let valorFinal = nuevoValor;
    const frecuencia = row ? (Number(row.frecuencia) || 1) : 1;
    const limiteContrato = row ? (Number(row.limiteCobertura) || 0) : 0;
    const montoMaximo = frecuencia * limiteContrato;
    const esMontoAbierto = esCoberturaMontoAbierto(codigoCobertura);

    // Coberturas con monto abierto no tienen validación de límite×frecuencia
    if (!esMontoAbierto && valorIngresado > montoMaximo) {
      valorFinal = String(montoMaximo);
      setAlertConfig({
        type: "info",
        title: "¡Atención!",
        message: `El monto reclamado no puede ser mayor a ${montoMaximo.toLocaleString("es-DO")} (Frecuencia × Límite Contrato).`,
        onConfirm: () => setShowAlert(false),
      });
      setShowAlert(true);
    }
    
    // Validar tope de cobertura 1096 antes de actualizar
    const resultadoValidacion1096 = validarTopeCobertura1096(Number(valorFinal), codigoCobertura, secuencia, (montoAjustado) => {
      valorFinal = String(montoAjustado);
    });
    
    lastMontoReclamadoEditRef.current = { cobertura: Number(codigoCobertura), secuencia, value: valorFinal };
    
    const montoFinalNum = Number(valorFinal) || 0;

    // Calcular total de las OTRAS filas (excluyendo la actual) para el límite por caso
    const limiteCasoVal = Number(encabezado?.limiteCaso) || 0;
    const totalOtrasFilas = rows.reduce((acc, r) => {
      if (Number(r.cobertura) === Number(codigoCobertura) && r.secuencia === secuencia) return acc;
      return acc + (Number(r.montoReclamadoCobertura) || 0);
    }, 0);
    
    // Actualizar la fila con el nuevo monto
    setRows((prev) =>
      prev.map((r) => {
        if (Number(r.cobertura) === Number(codigoCobertura) && r.secuencia === secuencia) {
          const porcientoCoaseguro = Number(r.porcientoCobertura) || 0;
          const tienePorcientoCobertura = r.porcientoCobertura !== undefined && r.porcientoCobertura !== null;

          // Si tenemos % coaseguro, calcular distribución considerando límite por caso
          if (tienePorcientoCobertura) {
            const distribucion = calcularDistribucionConLimiteCaso(montoFinalNum, porcientoCoaseguro, totalOtrasFilas, limiteCasoVal);
            
            return {
              ...r,
              montoReclamadoCobertura: valorFinal,
              diferenciaNoCubierta: distribucion.diferenciaNoCubierta,
              montoCoaseguro: distribucion.diferenciaNoCubierta,
              montoPagoAfiliado: distribucion.diferenciaNoCubierta,
              montoPagoCobertura: distribucion.montoPagoCobertura,
              excedenteCopago: distribucion.diferenciaNoCubierta,
            };
          }

          // Si no hay % coaseguro, solo actualizar el monto
          return { ...r, montoReclamadoCobertura: valorFinal };
        }
        return r;
      })
    );

  };

  const onChangeTipoContratoOxigeno = (tipoContrato) => {
    setContratoTipo(tipoContrato);
    LimpiarCalculoOxigeno();
  };

  const handleChangeLitros = (e) => {
    const value = e.target.value;

    if (value === "") {
      setCantidadLitros("");
      return;
    }

    // validar 6 enteros + 3 decimales
    if (!LITROS_REGEX.test(value)) return;

    setCantidadLitros(value);
  };

  const LimpiarCalculoOxigeno = () => {
    setCantidadLitros("");
    setMontoReclamadoOxigeno("");
    setFilaOxigenoEditando(null); // Limpiar estado de edición
  };

  const realizarCalculoOxigeno = (formOxigeno) => {
    calcularOxigeno({
      tipoContrato: formOxigeno?.contratoTipo,
      cantidadLitros: Number(formOxigeno?.cantidadLitros),
      monto: Number(formOxigeno?.montoReclamado),
      tarifa: Number(formOxigeno?.tarifa)
    });
  };

  const agregarOxigeno = () => {
    if (contratoTipo === TIPO_CONTRATO.LITRO) {
      if(isEmptyValue(cantidadLitros) || Number(cantidadLitros) === 0){
        setAlertConfig({
          type: "error",
          title: "¡Advertencia!",
          message: "Favor ingresar la cantidad de litros",
          onConfirm: () => {
            setShowAlert(false);
          },
        });
        setShowAlert(true);
        return;
      }
    }

    if(isEmptyValue(montoReclamadoOxigeno) || Number(montoReclamadoOxigeno) === 0){
        setAlertConfig({
          type: "error",
          title: "¡Advertencia!",
          message: "Favor ingresar el monto",
          onConfirm: () => {
            setShowAlert(false);
          },
        });
        setShowAlert(true);
        return;
    }

    setAlertConfig({
      type: "confirm",
      title: "¡Advertencia!",
      message: "¿Estás seguro de que deseas aplicar estos cambios? ",
      confirmText: "Sí",
      cancelText: "No",
      onConfirm: () => {
        setShowAlert(false);
        
        // Si estamos editando una fila existente, actualizar su monto
        if (filaOxigenoEditando) {
          const montoReclamado = Number(montoReclamadoOxigeno);
          const tienePorcientoCobertura = filaOxigenoEditando.porcientoCobertura !== undefined && 
                                          filaOxigenoEditando.porcientoCobertura !== null;
          
          // Para oxígeno, calcular localmente si ya fue validado (tiene porcientoCobertura, aunque sea 0)
          if (tienePorcientoCobertura) {
            const porcientoCoaseguro = Number(filaOxigenoEditando.porcientoCobertura);
            // Calcular total de OTRAS filas para límite por caso
            const limiteCasoOx = Number(encabezado?.limiteCaso) || 0;
            const totalOtrasOx = rows.reduce((acc, r) => {
              if (r.secuencia === filaOxigenoEditando.secuencia) return acc;
              return acc + (Number(r.montoReclamadoCobertura) || 0);
            }, 0);
            const distribucionOx = calcularDistribucionConLimiteCaso(montoReclamado, porcientoCoaseguro, totalOtrasOx, limiteCasoOx);

            setRows((prevRows) =>
              prevRows.map((row) =>
                row.secuencia === filaOxigenoEditando.secuencia
                  ? { 
                      ...row, 
                      montoReclamadoCobertura: montoReclamado,
                      diferenciaNoCubierta: distribucionOx.diferenciaNoCubierta,
                      montoCoaseguro: distribucionOx.diferenciaNoCubierta,
                      montoPagoAfiliado: distribucionOx.diferenciaNoCubierta,
                      montoPagoCobertura: distribucionOx.montoPagoCobertura,
                      excedenteCopago: distribucionOx.diferenciaNoCubierta,
                    }
                  : row
              )
            );
          } else {
            // Si no tiene porcientoCobertura, solo actualizar el monto
            setRows((prevRows) =>
              prevRows.map((row) =>
                row.secuencia === filaOxigenoEditando.secuencia
                  ? { ...row, montoReclamadoCobertura: montoReclamado }
                  : row
              )
            );
          }
          
          setFilaOxigenoEditando(null); // Limpiar el estado de edición

          // Mostrar alerta informativa si se superó el límite por caso después del cambio de oxígeno
          const limiteCasoOxPost = Number(encabezado?.limiteCaso) || 0;
          const totalDespuesOx = rows.reduce((acc, r) => {
            if (r.secuencia === filaOxigenoEditando.secuencia) return acc + montoReclamado;
            return acc + (Number(r.montoReclamadoCobertura) || 0);
          }, 0);
          setTimeout(() => {
            validarLimitePorCaso(totalDespuesOx);
          }, 200);
        } else {
          // Si no estamos editando, es una nueva cobertura
          onSelectCobertura(coberturaSeleccionada, true, Number(montoReclamadoOxigeno));
        }
        
        setIsModalOxigenoOpen(false);
        // Limpiar estado de la calculadora para el próximo uso
        setCantidadLitros("");
        setMontoReclamadoOxigeno("");
        setContratoTipo(TIPO_CONTRATO.LITRO);
        limpiarCalcularOxigeno?.();
      },
      onCancel: () => {
        setShowAlert(false);
      },
    });
    setShowAlert(true);
  };

  // Función interna para ejecutar el envío de reclamación
  const ejecutarEnvioReclamacion = () => {
    // Filtrar coberturas nuevas (en memoria) y las que vienen de hoja quirúrgica
    let coberturasEnMemoria = rows.filter(
      (r) => r.esNueva === true || r.vieneDeHojaQuirurgica === true
    );

    // La secuencia base es la cantidad de coberturas precargadas del backend (no tienen campo secuencia)
    const filasDetalleIngreso = rows.filter(
      (r) => r.esNueva !== true && !r.vieneDeHojaQuirurgica
    );
    const maxSecuenciaDetalle = filasDetalleIngreso?.length ?? 0;

    // Extraer año y secuencial directamente del numeroIngreso (formato: "2025-9659559")
    const numeroIngreso = encabezado?.numeroIngreso || "";
    const numeroIngresoParts = numeroIngreso.split("-");
    const anoIngreso = numeroIngresoParts.length >= 1 ? Number(numeroIngresoParts[0]) : 0;
    const secuencialIngreso = numeroIngresoParts.length >= 2 ? Number(numeroIngresoParts[1]) : 0;

    // Construir payloads: secuencia incremental continuando después del detalle (maxSecuenciaDetalle + 1, + 2, ...)
    const fechaActual = new Date().toISOString();
    const usuarioPss = `pss: ${prestadorCodigo}`;
    let coberturasPayload = coberturasEnMemoria.map((cob, index) => ({
      ano: anoIngreso,
      secuencial: secuencialIngreso,
      secuencia: maxSecuenciaDetalle + (index + 1),
      servicio: Number(tipoServicioFormulario) || 0,
      tipoCobertura: Number(cob.tipoCobertura) || 2,
      cobertura: Number(cob.cobertura) || 0,
      fechaServicio: cob.fechaServicio || fechaActual,
      frecuencia: Number(cob.frecuencia) || 1,
      montoReclamadoCobertura: Number(cob.montoReclamadoCobertura) || 0,
      reserva: Number(cob.reserva) || 0,
      estatus: Number(cob.estatus) || 1,
      limiteCobertura: Number(cob.limiteCobertura) || 0,
      tipoAfiliado: cob.tipoAfiliado || "NO_MEDICO",
      codigoCentro: Number(cob.codigoCentro) || 0,
      porcientoCobertura: Number(cob.porcientoCobertura) || 0,
      usuarioTransaccion: usuarioPss,
      fechaTransaccion: cob.fechaTransaccion || fechaActual,
      montoPagoCobertura: Number(cob.montoPagoCobertura) || 0,
      ramoIngreso: Number(cob.ramoIngreso) || RAMO_HOSPITALIZACION,
      montoPagoAfiliado: Number(cob.montoPagoAfiliado) || 0,
      excedenteCopago: Number(cob.excedenteCopago) || 0,
      fechaIngresoCobertura: cob.fechaIngresoCobertura || fechaActual,
      usuarioIngresoCobertura: usuarioPss,
      codigoCompania: Number(cob.codigoCompania || empresa?.CODIGO) || 30,
    }));

    // Si al validar se marcó incluir el draft (tiene cobertura válida), agregarlo al payload
    if (incluirDraftEnEnvioRef.current && draft.cobertura) {
      const secuenciaDraft = maxSecuenciaDetalle + coberturasPayload.length + 1;
      coberturasPayload = [
        ...coberturasPayload,
        {
          ano: anoIngreso,
          secuencial: secuencialIngreso,
          secuencia: secuenciaDraft,
          servicio: Number(tipoServicioFormulario) || 0,
          tipoCobertura: Number(draft.tipoCobertura) || 2,
          cobertura: Number(draft.cobertura) || 0,
          fechaServicio: draft.fechaServicio || fechaActual,
          frecuencia: Number(draft.frecuencia) || 1,
          montoReclamadoCobertura: Number(draft.montoReclamadoCobertura) || 0,
          reserva: Number(draft.reserva) || 0,
          estatus: Number(draft.estatus) || 61,
          limiteCobertura: Number(draft.limiteCobertura) || 0,
          tipoAfiliado: draft.tipoAfiliado || "NO_MEDICO",
          codigoCentro: Number(draft.codigoCentro) || 0,
          porcientoCobertura: Number(draft.porcientoCobertura) || 0,
          usuarioTransaccion: usuarioPss,
          fechaTransaccion: draft.fechaTransaccion || fechaActual,
          montoPagoCobertura: Number(draft.montoPagoCobertura) || 0,
          ramoIngreso: Number(draft.ramoIngreso) || RAMO_HOSPITALIZACION,
          montoPagoAfiliado: Number(draft.montoPagoAfiliado) || 0,
          excedenteCopago: Number(draft.excedenteCopago) || 0,
          fechaIngresoCobertura: draft.fechaIngresoCobertura || fechaActual,
          usuarioIngresoCobertura: usuarioPss,
          codigoCompania: Number(draft.codigoCompania || empresa?.CODIGO) || 30,
        },
      ];
      incluirDraftEnEnvioRef.current = false;
    }

    // Obtener datos del encabezado para cerrar cuenta
    const poliza = encabezado?.poliza || "";
    const polizaParts = poliza.split("-");
    const ramoPoliza = polizaParts.length >= 2 ? Number(polizaParts[1]) : 0;
    const secuencialPoliza = polizaParts.length >= 3 ? Number(polizaParts[2]) : 0;

    // Usar la fecha de egreso del formulario para fechaDia
    const fechaEgreso = fechaEgresoFormulario 
      ? new Date(`${fechaEgresoFormulario}T00:00:00`).toISOString()
      : fechaActual;

    // Construir payload para cerrar cuenta
    const payloadCerrarCuenta = {
      ano: anoIngreso,
      secuencial: secuencialIngreso,
      tipoReclamante: tipoPss || "NO_MEDICO",
      codigoReclamante: Number(prestadorCodigo) || 0,
      tipoAsegurado: encabezado?.tipoAsegurado || "",
      codigoAsegurado: Number(encabezado?.numeroAsegurado) || 0,
      frecuenciaAsegurado: Number(encabezado?.secuenciaDependiente) || 0,
      numeroPlastico: Number(encabezado?.numeroPlastico) || 0,
      codigoCompania: Number(empresa?.CODIGO) || 30,
      ramoPoliza: ramoPoliza,
      secuencialPoliza: secuencialPoliza,
      plan: Number(encabezado?.planPoliza) || 0,
      fechaDia: fechaEgreso,
      fechaTransaccion: fechaActual,
      usuario: usuarioPss,
      servicio: Number(tipoServicioFormulario || encabezado?.codigoServicio) || 0,
      riesgoLaboral: "",
      simultaneo: encabezado?.checkSimultaneidad || "N",
      procesoComentario: "",
      motivoEgreso: motivoEgresoFormulario ?? "",
    };

    // Preparar el payload para enviar
    const payloadEnvio = {
      coberturasEnMemoria: coberturasPayload,
      payloadCerrarCuenta,
    };

    // Guardar el payload para enviarlo después de la validación
    setPayloadEnvioReclamacion(payloadEnvio);

    // Primero validar si existe otro ingreso abierto
    const numeroIngresoPartsValidacion = (encabezado?.numeroIngreso || "").split("-");
    const secuencialIngresoValidacion = numeroIngresoPartsValidacion.length >= 2 ? Number(numeroIngresoPartsValidacion[1]) : (Number(secuencialFormulario) || 0);
    
    const payloadValidacion = {
      ano: Number(anioFormulario) || 0,
      secuencial: secuencialIngresoValidacion,
      numeroPlastico: Number(encabezado?.numeroPlastico) || 0,
      codigoAsegurado: Number(encabezado?.numeroAsegurado) || 0,
      codigoDependiente: Number(encabezado?.secuenciaDependiente) || 0,
    };

    validarIngresoExistente(payloadValidacion);
  };

  // Función para mostrar popup de confirmación antes de enviar reclamación
  const handleEnviarReclamacion = () => {
    if (isDisabled || enviarReclamacionLoading || validarIngresoExistenteLoading) return;

    // Validar campos requeridos (Tipo Servicio y Fecha Egreso)
    if (typeof validarCamposRequeridos === "function") {
      const esValido = validarCamposRequeridos();
      if (!esValido) {
        return;
      }
    }

    // Validar que no se pueda enviar reclamación con servicio Maternidad
    if (tipoServicioFormulario === "5") {
      setAlertConfig({
        type: "error",
        title: "¡Error!",
        message: "No se puede enviar una reclamación con servicio de Maternidad. Debe cambiar el tipo de servicio a Parto o Cesárea, o gestionar por CAC/Call Center.",
        onConfirm: () => setShowAlert(false),
      });
      setShowAlert(true);
      return;
    }

    // Si el draft tiene cobertura, validarlo y marcarlo para incluir en el envío
    incluirDraftEnEnvioRef.current = false;
    if (draft.cobertura) {
      const montoDraft = Number(draft.montoReclamadoCobertura) || 0;
      if (montoDraft <= 0) {
        setAlertConfig({
          type: "error",
          title: "¡Error de validación!",
          message: `La cobertura en edición con código ${draft.cobertura} debe tener monto reclamado mayor a 0. Complétela antes de enviar la reclamación o elimínela.`,
          onConfirm: () => setShowAlert(false),
        });
        setShowAlert(true);
        return;
      }
      if (validarCoberturaExistente(draft.cobertura)) {
        setAlertConfig({
          type: "error",
          title: "¡Error!",
          message: "La cobertura en edición ya existe en la tabla. Elimine el duplicado o limpie la fila antes de enviar.",
          onConfirm: () => setShowAlert(false),
        });
        setShowAlert(true);
        return;
      }
      if (validarCoberturaDuplicada(draft.cobertura, draft.tipoAfiliado, draft.codigoCentro)) {
        setAlertConfig({
          type: "error",
          title: "¡Error!",
          message: "No se puede enviar: la cobertura en edición (mismo reclamante médico) ya existe en la tabla.",
          onConfirm: () => setShowAlert(false),
        });
        setShowAlert(true);
        return;
      }
      incluirDraftEnEnvioRef.current = true;
    }

    // Validar que haya al menos una cobertura (filas o draft válido)
    if (rows.length === 0 && !incluirDraftEnEnvioRef.current) {
      setAlertConfig({
        type: "error",
        title: "¡Advertencia!",
        message: "Debe agregar al menos una cobertura para enviar la reclamación.",
        onConfirm: () => setShowAlert(false),
      });
      setShowAlert(true);
      return;
    }

    // Validar que todas las coberturas (filas) tengan monto reclamado > 0 (el draft ya se validó arriba)
    const coberturasConMontoInvalido = rows.filter((r) => {
      const monto = Number(r.montoReclamadoCobertura) || 0;
      return monto <= 0;
    });

    if (coberturasConMontoInvalido.length > 0) {
      const codigosInvalidos = coberturasConMontoInvalido
        .map((r) => `${r.cobertura} - ${r.descripcionCobertura}`)
        .join(", ");
      
      setAlertConfig({
        type: "error",
        title: "¡Error de validación!",
        message: `Las siguientes coberturas tienen monto reclamado inválido (debe ser mayor a 0): ${codigosInvalidos}. Por favor, corrija los montos antes de enviar la reclamación.`,
        onConfirm: () => setShowAlert(false),
      });
      setShowAlert(true);
      return;
    }

    // Verificar si la cuenta supera el límite por caso antes de confirmar el envío
    const limiteCasoEnvio = Number(encabezado?.limiteCaso) || 0;
    let totalCuentaEnvio = rows.reduce((acc, r) => acc + (Number(r.montoReclamadoCobertura) || 0), 0);
    if (incluirDraftEnEnvioRef.current) {
      totalCuentaEnvio += Number(draft.montoReclamadoCobertura) || 0;
    }
    const servicioEnvio = String(tipoServicioFormulario || encabezado?.codigoServicio || "");
    const esMaternidadEnvio =
      servicioEnvio === SERVICIO_MATERNIDAD ||
      servicioEnvio === SERVICIO_PARTO ||
      servicioEnvio === SERVICIO_CESAREA;

    const mostrarConfirmacionFinal = () => {
      setAlertConfig({
        type: "confirm",
        title: "¿Seguro que deseas cerrar la cuenta clínica?",
        message: "Al hacer clic en aceptar, no podrás modificar el cierre por esta vía, para mayor información favor de contactar con nuestro Centro de Atención al Cliente llamando al 809-476-3535 o desde el interior sin cargo al 1-809-200-8011.",
        confirmText: "Aceptar",
        cancelText: "Cancelar",
        onConfirm: () => {
          setShowAlert(false);
          ejecutarEnvioReclamacion();
        },
        onCancel: () => {
          setShowAlert(false);
        },
      });
      setShowAlert(true);
    };

    if (totalCuentaEnvio > limiteCasoEnvio) {
      const excedente = totalCuentaEnvio - limiteCasoEnvio;
      const mensajeLimite = esMaternidadEnvio
        ? `La cuenta supera el monto disponible de maternidad según el plan.\n\nMonto disponible: ${formatRD(limiteCasoEnvio)}\nTotal de la cuenta: ${formatRD(totalCuentaEnvio)}\nExcedente: ${formatRD(excedente)}`
        : `La cuenta supera el límite por caso.\n\nLímite por caso: ${formatRD(limiteCasoEnvio)}\nTotal de la cuenta: ${formatRD(totalCuentaEnvio)}\nExcedente: ${formatRD(excedente)}`;
      setAlertConfig({
        type: "warning",
        title: "Advertencia",
        message: mensajeLimite,
        confirmText: "Continuar",
        cancelText: "Cancelar",
        onConfirm: () => {
          setShowAlert(false);
          mostrarConfirmacionFinal();
        },
        onCancel: () => {
          setShowAlert(false);
        },
      });
      setShowAlert(true);
      return;
    }

    mostrarConfirmacionFinal();
  };

  // Efecto para manejar respuesta de validar ingreso existente
  useUpdateEffect(() => {
    if (!isObjectEmpty(validarIngresoExistenteData)) {
      const existeOtraHospitalizacion = validarIngresoExistenteData?.existeOtraHospitalizacion;

      // Limpiar la validación
      limpiarValidarIngresoExistente?.();

      if (existeOtraHospitalizacion === true) {
        // Bloquear el formulario y mostrar mensaje
        setFormularioBloqueadoPorCobertura(true);
        setAlertConfig({
          type: "error",
          title: "¡Error!",
          message: "Existe un ingreso/cuenta clínica abierto para este asegurado en este u otro centro. Debe cerrarse/regularizarse antes de continuar. Gestionar por CAC/Call Center si requiere asistencia.",
          onConfirm: () => {
            setShowAlert(false);
          },
        });
        setShowAlert(true);
        // No continuar con el envío
        setPayloadEnvioReclamacion(null);
      } else {
        // No existe otra hospitalización, continuar con el envío
        if (payloadEnvioReclamacion) {
          enviarReclamacion(payloadEnvioReclamacion);
          setPayloadEnvioReclamacion(null);
        }
      }
    }
  }, [validarIngresoExistenteData]);

  // Efecto para manejar error de validar ingreso existente
  useUpdateEffect(() => {
    if (!isObjectEmpty(validarIngresoExistenteError)) {
      const mensaje =
        validarIngresoExistenteError?.mensaje ||
        "Error al validar ingreso existente.";

      limpiarValidarIngresoExistente?.();
      setPayloadEnvioReclamacion(null);

      setAlertConfig({
        type: "error",
        title: "Error",
        message: mensaje,
        onConfirm: () => {
          setShowAlert(false);
        },
      });
      setShowAlert(true);
    }
  }, [validarIngresoExistenteError]);

  // Efecto para manejar respuesta exitosa de enviar reclamación
  useUpdateEffect(() => {
    if (!isObjectEmpty(enviarReclamacionData)) {
      const mensaje =
        enviarReclamacionData?.mensaje ||
        "La cuenta clínica ha sido cerrada satisfactoriamente. A partir de este momento, los datos ingresados no podrán ser modificados desde WebSalud. Si necesita realizar un ajuste o corrección, por favor contacte a nuestro Centro de Atención al Cliente.";

      setAlertConfig({
        type: "success",
        title: "¡Éxito!",
        message: mensaje,
        onConfirm: () => {
          setShowAlert(false);
          limpiarEnviarReclamacion?.();
          // Limpiar las filas ya que se enviaron
          setRows([]);
          limpiarDraft();
          // Redirigir a la pantalla de búsqueda de cierres de cuentas
          history.push("/busqueda-avanzada-egresos");
        },
      });
      setShowAlert(true);
    }
  }, [enviarReclamacionData]);

  // Efecto para manejar error de enviar reclamación
  useUpdateEffect(() => {
    if (!isObjectEmpty(enviarReclamacionError)) {
      const tipoError = enviarReclamacionError?.tipoError;
      const mensaje =
        enviarReclamacionError?.mensaje ||
        "Error al enviar la reclamación.";

      // Determinar el título según el tipo de error
      let titulo = "¡Advertencia!";
      if (tipoError === "insercion") {
        titulo = "Error al insertar cobertura";
      } else if (tipoError === "cierreCuenta") {
        titulo = "Error al cerrar cuenta clínica";
      }

      setAlertConfig({
        type: "error",
        title: titulo,
        message: mensaje,
        onConfirm: () => {
          setShowAlert(false);
          limpiarEnviarReclamacion?.();
        },
      });
      setShowAlert(true);
    }
  }, [enviarReclamacionError]);

  useUpdateEffect(() => {
    if (!isObjectEmpty(eliminarCoberturaData)) {
      const mensaje =
        eliminarCoberturaData?.Mensaje ||
        "Cobertura eliminada exitosamente.";

      // Eliminar la fila del estado local usando el código de cobertura guardado
      if (coberturaEliminandoRef.current) {
        const codigoCobertura = coberturaEliminandoRef.current;
        setRows((prev) => prev.filter((r) => Number(r.cobertura) !== codigoCobertura));
        coberturaEliminandoRef.current = null;
      }

      setAlertConfig({
        type: "success",
        title: "¡Éxito!",
        message: mensaje,
        onConfirm: () => {
          setShowAlert(false);
          limpiarEliminarCobertura();
        },
      });
      setShowAlert(true);
    }
  }, [eliminarCoberturaData, limpiarEliminarCobertura]);

  useUpdateEffect(() => {
    if (!isObjectEmpty(eliminarCoberturaError)) {
      const { mensaje } = eliminarCoberturaError;
      
      // Limpiar el ref si hay error
      coberturaEliminandoRef.current = null;
      
      setAlertConfig({
        type: "error",
        title: "¡Advertencia!",
        message: mensaje,
        onConfirm: () => {
          setShowAlert(false);
          limpiarEliminarCobertura();
        },
      });
      setShowAlert(true);
    }
  }, [eliminarCoberturaError, limpiarEliminarCobertura]);

  useUpdateEffect(() => {
    if (!isObjectEmpty(validarCoberturaData)) {
      const data = validarCoberturaData?.data || validarCoberturaData;
      
      const error = data?.error ?? data?.Error;
      if (error === 0 || error === null || error === undefined) {
        // Verificar si se está intentando agregar una nueva fila
        // Verificar si se está validando una fila existente
        const filaValidando = filaValidandoRef.current;
        if (filaValidando && filaValidando.cobertura) {
          // Calcular total de las OTRAS filas (excluyendo la que se está validando) para el límite por caso
          const limiteCasoVal = Number(encabezado?.limiteCaso) || 0;
          const totalOtrasFilas = rows.reduce((acc, r) => {
            if (Number(r.cobertura) === Number(filaValidando.cobertura)) return acc;
            return acc + (Number(r.montoReclamadoCobertura) || 0);
          }, 0);

          // Actualizar la fila específica en la tabla
          setRows((prev) =>
            prev.map((r) => {
              if (Number(r.cobertura) !== Number(filaValidando.cobertura)) {
                return r;
              }
              
              // Para coberturas con monto abierto, solo actualizar porcientoCobertura y límite (no sobrescribir montos)
              const esMontoAbierto = esCoberturaMontoAbierto(r.cobertura);
              const tieneCalculoLocal = r.montoReclamadoCobertura > 0 && 
                                       (r.montoPagoCobertura > 0 || r.diferenciaNoCubierta > 0 || 
                                        (r.porcientoCobertura !== undefined && r.porcientoCobertura !== null));
              
              if (esMontoAbierto && tieneCalculoLocal) {
                return {
                  ...r,
                  limiteCobertura: data?.limiteAfiliado != null ? Number(data.limiteAfiliado) : r.limiteCobertura,
                  // Mantener siempre la frecuencia del frontend, ignorar la del backend
                  frecuencia: r.frecuencia,
                  frecuenciaMaxima: data?.frecuenciaMaxima != null
                    ? Number(data.frecuenciaMaxima)
                    : (r.frecuenciaMaxima ?? null),
                  porcientoCobertura: data?.porcientoCoaseguro != null ? Number(data.porcientoCoaseguro) : r.porcientoCobertura,
                  reserva: data?.reserva != null ? Number(data.reserva) : r.reserva,
                  // Para 1083 no se usa límite×frecuencia; se deja el actual o 0
                  montoReclamadoCobertura: Number(r.montoReclamadoCobertura) || 0,
                  // NO actualizar los campos calculados
                };
              }
              
              // Para otras coberturas, actualizar normalmente
              const limiteCobertura = data?.limiteAfiliado != null ? Number(data.limiteAfiliado) : r.limiteCobertura;
              // Mantener siempre la frecuencia del frontend, ignorar la del backend
              const frecuencia = r.frecuencia;
              const frecuenciaMaxima = data?.frecuenciaMaxima != null
                ? Number(data.frecuenciaMaxima)
                : (r.frecuenciaMaxima ?? null);
              const porcientoCoaseguro = data?.porcientoCoaseguro != null ? Number(data.porcientoCoaseguro) : r.porcientoCobertura;
              
              // Coberturas con monto abierto no usan límite×frecuencia; el resto sí
              // Cobertura 1064 tampoco usa límite×frecuencia, mantiene el valor actual o 0
              const montoReclamadoPorDefecto = (esCoberturaMontoAbierto(r.cobertura) || r.cobertura === COBERTURA_HABITACION)
                ? (montoReclamadoEnviadoRef.current != null ? Number(montoReclamadoEnviadoRef.current) : (Number(r.montoReclamadoCobertura) || 0))
                : (montoReclamadoEnviadoRef.current != null
                  ? Number(montoReclamadoEnviadoRef.current)
                  : (r.montoReclamadoCobertura || (limiteCobertura * frecuencia) || 0));
              
              // Calcular distribución ARS/Afiliado considerando límite por caso
              const distribucion = calcularDistribucionConLimiteCaso(Number(montoReclamadoPorDefecto), Number(porcientoCoaseguro), totalOtrasFilas, limiteCasoVal);
              
              return {
                ...r,
                limiteCobertura: limiteCobertura,
                frecuencia: frecuencia,
                frecuenciaMaxima: frecuenciaMaxima,
                montoReclamadoCobertura: montoReclamadoPorDefecto,
                montoPagoCobertura: distribucion.montoPagoCobertura,
                diferenciaNoCubierta: distribucion.diferenciaNoCubierta,
                montoCoaseguro: distribucion.diferenciaNoCubierta,
                montoPagoAfiliado: distribucion.diferenciaNoCubierta,
                porcientoCobertura: porcientoCoaseguro,
                reserva: data?.reserva != null ? Number(data.reserva) : r.reserva,
                excedenteCopago: distribucion.diferenciaNoCubierta,
              };
            })
          );
          filaValidandoRef.current = null;
          montoReclamadoEnviadoRef.current = null;

          // Mostrar alerta informativa si se superó el límite por caso
          const limiteCasoRows = Number(encabezado?.limiteCaso) || 0;
          const totalDespuesRows = rows.reduce((acc, r) => acc + (Number(r.montoReclamadoCobertura) || 0), 0);
          validarLimitePorCaso(totalDespuesRows);
        } else {
          // Actualizar el draft (nueva cobertura)
          // Calcular total de todas las filas existentes para el límite por caso
          const limiteCasoDraft = Number(encabezado?.limiteCaso) || 0;
          const totalFilasParaDraft = rows.reduce((acc, r) => acc + (Number(r.montoReclamadoCobertura) || 0), 0);

          setDraft((prev) => {
            // Para coberturas con monto abierto, solo actualizar porcientoCobertura y límite
            const esMontoAbierto = esCoberturaMontoAbierto(prev.cobertura);
            const tieneCalculoLocal = prev.montoReclamadoCobertura > 0 && 
                                     (prev.montoPagoCobertura > 0 || prev.diferenciaNoCubierta > 0 || 
                                      (prev.porcientoCobertura !== undefined && prev.porcientoCobertura !== null));
            
            if (esMontoAbierto && tieneCalculoLocal) {
              return {
                ...prev,
                limiteCobertura: data?.limiteAfiliado != null ? Number(data.limiteAfiliado) : prev.limiteCobertura,
                // Mantener siempre la frecuencia del frontend, ignorar la del backend
                frecuencia: prev.frecuencia,
                frecuenciaMaxima: data?.frecuenciaMaxima != null
                  ? Number(data.frecuenciaMaxima)
                  : (prev.frecuenciaMaxima ?? null),
                porcientoCobertura: data?.porcientoCoaseguro != null ? Number(data.porcientoCoaseguro) : prev.porcientoCobertura,
                reserva: data?.reserva != null ? Number(data.reserva) : prev.reserva,
                // NO actualizar los campos calculados (montoPagoCobertura, diferenciaNoCubierta, etc.)
              };
            }
            
            // Para otras coberturas o oxígeno sin cálculo previo, actualizar todo normalmente
            const limiteCobertura = data?.limiteAfiliado != null ? Number(data.limiteAfiliado) : prev.limiteCobertura;
            // Mantener siempre la frecuencia del frontend, ignorar la del backend
            const frecuencia = prev.frecuencia;
            const frecuenciaMaxima = data?.frecuenciaMaxima != null
              ? Number(data.frecuenciaMaxima)
              : (prev.frecuenciaMaxima ?? null);
            const porcientoCoaseguro = data?.porcientoCoaseguro != null ? Number(data.porcientoCoaseguro) : prev.porcientoCobertura;
            
            // Coberturas con monto abierto no usan límite×frecuencia; el resto sí
            // Cobertura 1064 tampoco usa límite×frecuencia, mantiene el valor actual o 0
            const montoReclamadoPorDefecto = (esCoberturaMontoAbierto(prev.cobertura) || prev.cobertura === COBERTURA_HABITACION)
              ? (Number(prev.montoReclamadoCobertura) || 0)
              : (prev.montoReclamadoCobertura || (limiteCobertura * frecuencia) || 0);
            
            // Calcular distribución ARS/Afiliado considerando límite por caso
            const distribucion = calcularDistribucionConLimiteCaso(Number(montoReclamadoPorDefecto), Number(porcientoCoaseguro), totalFilasParaDraft, limiteCasoDraft);
            
            return {
              ...prev,
              limiteCobertura: limiteCobertura,
              frecuencia: frecuencia,
              frecuenciaMaxima: frecuenciaMaxima,
              montoReclamadoCobertura: montoReclamadoPorDefecto,
              montoPagoCobertura: distribucion.montoPagoCobertura,
              diferenciaNoCubierta: distribucion.diferenciaNoCubierta,
              montoCoaseguro: distribucion.diferenciaNoCubierta,
              montoPagoAfiliado: distribucion.diferenciaNoCubierta,
              porcientoCobertura: porcientoCoaseguro,
              reserva: data?.reserva != null ? Number(data.reserva) : prev.reserva,
              excedenteCopago: distribucion.diferenciaNoCubierta,
            };
          });

          // Mostrar alerta informativa si se superó el límite por caso (draft)
          // Para coberturas de monto abierto, la alerta se muestra al asignar el monto (calculadora/input)
          if (!esCoberturaMontoAbierto(draft.cobertura)) {
            // Calcular el monto esperado del draft (mismo cálculo que dentro del setDraft)
            const limCob = data?.limiteAfiliado != null ? Number(data.limiteAfiliado) : (Number(draft.limiteCobertura) || 0);
            const freq = Number(draft.frecuencia) || 1;
            const montoEsperado = draft.montoReclamadoCobertura || (limCob * freq) || 0;
            const totalConDraft = totalFilasParaDraft + Number(montoEsperado);
            setTimeout(() => {
              validarLimitePorCaso(totalConDraft);
            }, 200);
          }
        }
        limpiarValidarCobertura();
      } else {
        const mensaje = data?.mensaje || data?.Mensaje || "Error al validar la cobertura.";
        limpiarDraft();
        filaValidandoRef.current = null;
        montoReclamadoEnviadoRef.current = null;
        setAlertConfig({
          type: "error",
          title: "¡Advertencia!",
          message: mensaje,
          onConfirm: () => {
            setShowAlert(false);
            limpiarValidarCobertura();
          },
        });
        setShowAlert(true);
      }
    }
  }, [validarCoberturaData, limpiarValidarCobertura]);

  useUpdateEffect(() => {
    if (!isObjectEmpty(validarCoberturaError)) {
      const codigo = validarCoberturaError?.codigo;
      const mensaje =
        validarCoberturaError?.mensaje || "Error al validar la cobertura.";
      
      // Caso especial: código 100 - cobertura no contratada para el PSS no médico
      // Caso especial: código 101 - cobertura no contratada para el PSS médico
      if (codigo === 100 || codigo === 101) {
        setAlertConfig({
          type: "confirm",
          title: "¡Advertencia!",
          message: "La cobertura indicada no se encuentra contratada para el PSS. ¿Estás seguro de que deseas continuar sin la cobertura digitada?",
          confirmText: "Sí",
          cancelText: "No",
          onConfirm: () => {
            // Si elige "Sí", cerrar popup y continuar normalmente
            setShowAlert(false);
            limpiarDraft();
            limpiarValidarCobertura();
            filaValidandoRef.current = null;
            montoReclamadoEnviadoRef.current = null;
          },
          onCancel: () => {
            // Si elige "No", mostrar mensaje de contactar al centro de atención
            setShowAlert(false);
            limpiarDraft();
            limpiarValidarCobertura();
            filaValidandoRef.current = null;
            montoReclamadoEnviadoRef.current = null;
            setTimeout(() => {
              setAlertConfig({
                type: "info",
                title: "Información",
                message: "Para validar y completar el egreso, le solicitamos comunicarse con nuestro Centro de Atención al Cliente al 809-476-3535, opción 1 y luego opción 1.",
                onConfirm: () => {
                  setShowAlert(false);
                  // Bloquear el formulario después de confirmar el mensaje
                  setFormularioBloqueadoPorCobertura(true);
                },
              });
              setShowAlert(true);
            }, 100);
          },
        });
        setShowAlert(true);
        return;
      }
      
      // Otros errores: comportamiento normal
      limpiarDraft();
      filaValidandoRef.current = null;
      montoReclamadoEnviadoRef.current = null;
      setAlertConfig({
        type: "error",
        title: "¡Advertencia!",
        message: mensaje,
        onConfirm: () => {
          setShowAlert(false);
          limpiarValidarCobertura();
        },
      });
      setShowAlert(true);
    }
  }, [validarCoberturaError, limpiarValidarCobertura]);

  useUpdateEffect(() => {
    if (!isObjectEmpty(calcularOxigenoData)) {
      if(calcularOxigenoData?.success){
        setCantidadLitros(calcularOxigenoData?.cantidadLitros || 0);
        setMontoReclamadoOxigeno(calcularOxigenoData?.montoReclamado || 0);
      }else{
        setAlertConfig({
          type: "error",
          title: "¡Advertencia!",
          message: calcularOxigenoData?.message,
          onConfirm: () => {
            setShowAlert(false);
            limpiarValidarCobertura();
          },
        });
        setShowAlert(true);
      }
    }
  }, [calcularOxigenoData]);

  useEffect(() => {
    return () => {
      if (validacionTimeoutRef.current) {
        clearTimeout(validacionTimeoutRef.current);
      }
      if (debounceCodigoCoberturaRef.current) {
        clearTimeout(debounceCodigoCoberturaRef.current);
      }
    };
  }, []);

  // Listener para insertar coberturas de Hoja Quirúrgica cuando están completadas
  useEffect(() => {
    const handleInsertarCoberturasHojaQX = async (event) => {
      // Si el ingreso está convertido, no procesar nada
      const ingresoConvertido = encabezado?.estatusDesc === "CONVERTIDO";
      if (ingresoConvertido) {
        return; // No procesar nada más
      }
      
      const { coberturas } = event.detail || {};
      if (!coberturas || !Array.isArray(coberturas) || coberturas.length === 0) return;
      
      const coberturasConError = [];
      
      // Obtener coberturas del detalle de ingreso original (inmutable)
      const ingresoDetalle = consultarIngresosPssData?.ingresoDetalle || [];
      
      // Procesar cada cobertura
      for (const cobertura of coberturas) {
        try {
          // Mapear campos correctos de la respuesta de Hoja Quirúrgica
          const codigoCobertura = Number(cobertura.cobRecl || 0);
          const tipoCobertura = Number(cobertura.tipCobRec || 0);
          const frecuencia = 1; // Siempre 1 para coberturas de Hoja Quirúrgica
          const montoReclamado = 0; // No viene en la respuesta
          
          // Validar si ya existe en el detalle de ingreso del backend (inmutable)
          const existeEnDetalleIngreso = ingresoDetalle.some(
            (det) => Number(det.cobertura) === codigoCobertura && Number(det.tipoCobertura || 2) === tipoCobertura
          );
          
          if (existeEnDetalleIngreso) {
            continue; // Saltar si ya existe en el backend
          }
          
          // Validar si ya existe en la tabla de coberturas nuevas (en memoria)
          const existeEnTabla = rows.some(
            (row) => Number(row.cobertura) === codigoCobertura && Number(row.tipoCobertura) === tipoCobertura
          );
          
          if (existeEnTabla) {
            continue; // Saltar si ya existe en tabla
          }
          
          // Determinar reclamante: si no tiene codMedicoRecl o es "0", usar PSS logueado
          const codMedico = Number(cobertura.codMedicoRecl || 0);
          const tieneReclamanteValido = codMedico > 0;
          
          const codigoReclamante = tieneReclamanteValido ? codMedico : Number(prestadorCodigo);
          const nombreReclamante = tieneReclamanteValido ? cobertura.nombreMedicoRecl : prestadorNombre;
          const tipoReclamante = tieneReclamanteValido ? "MEDICO" : "NO_MEDICO";
          
          // Construir objeto de cobertura para validación
          const coberturaObj = {
            cobertura: codigoCobertura,
            descripcionCobertura: cobertura.descCobertura || "",
            tipoCobertura: tipoCobertura,
          };
          
          // Construir payload de validación
          const payload = construirPayloadValidacion(
            coberturaObj,
            frecuencia,
            montoReclamado,
            {
              tipoAfiliado: tipoReclamante,
              codigoCentro: codigoReclamante,
            }
          );
          
          if (!payload) {
            continue;
          }
          
          // Llamar al servicio de validación directamente
          const EgresosService = require("../../../services/egresos/EgresosService").default;
          const response = await EgresosService.validarCobertura(payload);
          
          // Procesar respuesta de validación
          // Verificar primero si hay error en respuesta.codigo (estructura del backend)
          const codigoRespuesta = response?.respuesta?.codigo;
          const mensajeRespuesta = response?.respuesta?.mensaje;
          const tieneErrorEnRespuesta = codigoRespuesta !== undefined && codigoRespuesta !== 0;
          
          // También verificar error en data (estructura alternativa)
          const data = response?.data || response;
          const error = data?.error ?? data?.Error;
          
          let limiteCobertura = 0;
          let porcientoCobertura = 0;
          let errorValidacion = null;
          
          // Validación exitosa: no hay error en respuesta.codigo Y no hay error en data
          if (!tieneErrorEnRespuesta && (error === 0 || error === null || error === undefined)) {
            limiteCobertura = Number(data?.limiteAfiliado || 0);
            porcientoCobertura = Number(data?.porcientoCoaseguro || 0);
            
            // Coberturas con monto abierto no usan límite×frecuencia (monto se define después)
            const montoReclamadoCobertura = esCoberturaMontoAbierto(codigoCobertura)
              ? 0
              : limiteCobertura * frecuencia;
            const diferenciaNoCubierta = montoReclamadoCobertura * (porcientoCobertura / 100);
            const montoPagoCobertura = montoReclamadoCobertura - diferenciaNoCubierta;
            
            // Crear y agregar la fila a la tabla solo si la validación fue exitosa
            // vieneDeHojaQuirurgica: identificar origen para habilitar eliminar
            const nuevaFila = {
              secuencia: calcularSiguienteSecuencia(),
              cobertura: codigoCobertura,
              descripcionCobertura: cobertura.descCobertura || "",
              tipoCobertura: tipoCobertura,
              servicio: Number(tipoServicioFormulario) || 0,
              tipoAfiliado: tipoReclamante,
              codigoCentro: codigoReclamante,
              nombreReclamante: nombreReclamante,
              frecuencia: frecuencia,
              montoReclamadoCobertura,
              diferenciaNoCubierta,
              montoPagoCobertura,
              limiteCobertura,
              porcientoCobertura,
              esNueva: false,
              vieneDeHojaQuirurgica: true, // Identifica origen HQ → botón eliminar activo
            };
            
            setRows((prev) => [...prev, nuevaFila]);
          } else {
            // Si la validación falló, NO agregar la cobertura y guardar el error
            // Priorizar mensaje de respuesta.mensaje, luego data.mensaje
            errorValidacion = mensajeRespuesta || data?.mensaje || "Error al validar la cobertura";
            coberturasConError.push({
              codigoCobertura,
              descripcion: cobertura.descCobertura,
              error: errorValidacion,
            });
          }
          
        } catch (error) {
          console.error("Error al procesar cobertura de Hoja QX:", error);
          coberturasConError.push({
            codigoCobertura: cobertura.cobRecl,
            descripcion: cobertura.descCobertura,
            error: error.message || "Error desconocido",
          });
        }
      }
      
      // Si hubo errores, mostrar alerta con resumen
      if (coberturasConError.length > 0) {
        const mensajeErrores = coberturasConError
          .map((c) => `• Cobertura ${c.codigoCobertura} - ${c.descripcion}:\n  ${c.error}`)
          .join("\n\n");
        
        setAlertConfig({
          type: "error",
          title: "Coberturas de Hoja Quirúrgica no agregadas",
          message: `Las siguientes coberturas de la validación de Hoja Quirúrgica no se pudieron agregar:\n\n${mensajeErrores}`,
          onConfirm: () => setShowAlert(false),
        });
        setShowAlert(true);
      }
    };
    
    window.addEventListener('insertarCoberturasHojaQX', handleInsertarCoberturasHojaQX);
    
    return () => {
      window.removeEventListener('insertarCoberturasHojaQX', handleInsertarCoberturasHojaQX);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, encabezado, prestadorCodigo, prestadorNombre, tipoServicioFormulario, consultarIngresosPssData]);

  const totals = useMemo(() => {
    let totalARS = rows.reduce((acc, r) => acc + toNumber(r?.montoPagoCobertura), 0);
    let totalAsegurado = rows.reduce((acc, r) => acc + calcularDifNoCubierta(r), 0);
    let totalReclamado = rows.reduce((acc, r) => acc + toNumber(r?.montoReclamadoCobertura), 0);
    // Incluir el draft en los totales cuando tiene una cobertura asociada
    if (draft.cobertura) {
      totalARS += toNumber(draft?.montoPagoCobertura) || 0;
      totalAsegurado += calcularDifNoCubierta(draft) || 0;
      totalReclamado += toNumber(draft?.montoReclamadoCobertura) || 0;
    }
    return { totalARS, totalAsegurado, totalReclamado };
  }, [rows, draft.cobertura, draft.montoPagoCobertura, draft.montoReclamadoCobertura, draft.diferenciaNoCubierta, draft.montoCoaseguro]);

  // Calcular totales por reclamante (incluye draft cuando tiene cobertura)
  const totalesPorReclamante = useMemo(() => {
    const agrupado = {};
    
    rows.forEach((row) => {
      const key = `${row.codigoCentro}-${row.nombreReclamante}`;
      if (!agrupado[key]) {
        agrupado[key] = {
          codigoReclamante: row.codigoCentro,
          nombreReclamante: row.nombreReclamante,
          totalReclamado: 0,
          totalARS: 0,
          totalAsegurado: 0,
        };
      }
      agrupado[key].totalReclamado += toNumber(row?.montoReclamadoCobertura);
      agrupado[key].totalARS += toNumber(row?.montoPagoCobertura);
      agrupado[key].totalAsegurado += calcularDifNoCubierta(row);
    });
    
    if (draft.cobertura) {
      const key = `${draft.codigoCentro}-${draft.nombreReclamante}`;
      if (!agrupado[key]) {
        agrupado[key] = {
          codigoReclamante: draft.codigoCentro,
          nombreReclamante: draft.nombreReclamante,
          totalReclamado: 0,
          totalARS: 0,
          totalAsegurado: 0,
        };
      }
      agrupado[key].totalReclamado += toNumber(draft?.montoReclamadoCobertura) || 0;
      agrupado[key].totalARS += toNumber(draft?.montoPagoCobertura) || 0;
      agrupado[key].totalAsegurado += calcularDifNoCubierta(draft) || 0;
    }
    
    return Object.values(agrupado);
  }, [rows, draft.cobertura, draft.codigoCentro, draft.nombreReclamante, draft.montoReclamadoCobertura, draft.montoPagoCobertura, draft.diferenciaNoCubierta, draft.montoCoaseguro]);

  // Deshabilitar "Cirugías adicionales" solo si hay coberturas en memoria agregadas por el usuario (esNueva === true)
  const tieneCoberturasNuevasEnMemoria = useMemo(
    () => Array.isArray(rows) && rows.some((r) => r.esNueva === true),
    [rows]
  );

  // Función para manejar el click en "Cirugías adicionales"
  const handleCirugiasAdicionales = () => {
    // Extraer el secuencial del número de ingreso
    const numeroIngreso = encabezado?.numeroIngreso || "";
    const numeroIngresoParts = numeroIngreso.split("-");
    const secuencial = numeroIngresoParts.length >= 2 ? numeroIngresoParts[1] : "";
    
    if (!secuencial) {
      setAlertConfig({
        type: "error",
        title: "¡Error!",
        message: "No se pudo obtener el número de ingreso para validar la hoja quirúrgica.",
        onConfirm: () => setShowAlert(false),
      });
      setShowAlert(true);
      return;
    }

    // Mostrar mensaje informativo antes de abrir la ventana
    setAlertConfig({
      type: "confirm",
      title: "¡Atención!",
      message: "Al crear la solicitud para la validación de cirugías adicionales, el proceso de alta médica por WebSalud quedará temporalmente detenido hasta recibir la respuesta del área médica.",
      confirmText: "Sí",
      cancelText: "No",
      onConfirm: () => {
        setShowAlert(false);
        // Abrir nueva ventana con la ruta de validación de hoja quirúrgica
        window.open(`/validar-hoja-QX/${secuencial}`, "_blank");
      },
      onCancel: () => {
        setShowAlert(false);
        // Si selecciona "No", simplemente cerrar el alert y dejar al usuario en el formulario
      },
    });
    setShowAlert(true);
  };

  return (
    <>
      <Header>
        <Title>Coberturas y Honorarios Reclamados</Title>
        <GreenButton type="button" disabled={isDisabled || tieneCoberturasNuevasEnMemoria} onClick={handleCirugiasAdicionales}>
          ¿Cirugías adicionales?
        </GreenButton>
      </Header>
      <TableOuter>
        <ScrollX>
          <CoveragesTable>
            <thead>
              <tr>
                <th style={{ width: "9%" }}>Cód. Proc.</th>
                <th style={{ width: "12%" }}>Desc. Proc.</th>
                <th>Tipo Reclamante</th>
                <th>Cód. Reclamante</th>
                <th>Reclamante</th>
                <th style={{ width: "10%" }}>Límite Contrato</th>
                <th style={{ width: "6%" }}>Frec.</th>
                <th>Monto Reclamado</th>
                <th>Monto Pagar ARS</th>
                <th>Dif. No Cub.</th>
                <th style={{ width: "7%" }}>% Coas.</th>
                <th style={{ width: "7%" }}>Acción</th>
              </tr>
            </thead>

            <tbody>
              {rows.map((r) => {
                return (
                <tr key={r.cobertura}>
                  <td>
                    <ReadonlyInput value={r.cobertura} readOnly />
                  </td>
                  <td>
                    <ReadonlyInput value={r.descripcionCobertura} readOnly />
                  </td>
                  <td>
                    <Select value={r.tipoAfiliado} disabled>
                      <option value="MEDICO">Médico</option>
                      <option value="NO_MEDICO">No Médico</option>
                    </Select>
                  </td>
                  <td>
                    <ReadonlyInput value={r.codigoCentro} readOnly />
                  </td>
                  <td>
                    <ReadonlyInput value={r.nombreReclamante} readOnly />
                  </td>
                  <td>
                    <ReadonlyInput 
                      value={Number(r.cobertura) === COBERTURA_HABITACION ? displayRD(0) : displayRD(r.limiteCobertura)} 
                      readOnly 
                    />
                  </td>
                  <td>
                    <ReadonlyInput 
                      value={r.frecuencia} 
                      readOnly 
                      title={esCoberturaMontoAbierto(r.cobertura) ? "La frecuencia de coberturas con monto abierto siempre es 1" : ""}
                    />
                  </td>
                  <td>
                    {esVigente && (r.esNueva || r.vieneDeHojaQuirurgica) ? (
                      <Input
                        type="text"
                        value={r.montoReclamadoCobertura || ""}
                        disabled={isDisabled || r.vieneDeHojaQuirurgica}
                        onClick={() => {
                          // Si es cobertura de oxígeno, abrir calculadora
                          if (Number(r.cobertura) === COBERTURA_OXIGENO) {
                            setCoberturaSeleccionada({
                              cobertura: r.cobertura,
                              descripcionCobertura: r.descripcionCobertura,
                              tipoCobertura: r.tipoCobertura,
                              limite: r.limiteCobertura,
                              porcientoCobertura: r.porcientoCobertura, // Guardar el % Coaseguro
                            });
                            // Pre-cargar valores actuales
                            setMontoReclamadoOxigeno(r.montoReclamadoCobertura || "");
                            setCantidadLitros("");
                            setContratoTipo(TIPO_CONTRATO.LITRO);
                            setFilaOxigenoEditando(r); // Guardar la fila que estamos editando
                            setIsModalOxigenoOpen(true);
                          }
                        }}
                        onChange={(e) => handleMontoReclamadoChange(r.cobertura, e.target.value, r.secuencia, r)}
                        onBlur={() => {
                          // Para coberturas con monto abierto, calcular localmente sin llamar a validarCobertura
                          const esMontoAbierto = esCoberturaMontoAbierto(r.cobertura);
                          const tienePorcientoCobertura = r.porcientoCobertura !== undefined && 
                                                          r.porcientoCobertura !== null;
                          
                          if (esMontoAbierto && tienePorcientoCobertura && r.montoReclamadoCobertura) {
                            const montoReclamado = Number(r.montoReclamadoCobertura);
                            const porcientoCoaseguro = Number(r.porcientoCobertura);
                            // Calcular total de OTRAS filas para límite por caso
                            const limiteCasoBlur = Number(encabezado?.limiteCaso) || 0;
                            const totalOtrasBlur = rows.reduce((acc, row) => {
                              if (Number(row.cobertura) === Number(r.cobertura) && row.secuencia === r.secuencia) return acc;
                              return acc + (Number(row.montoReclamadoCobertura) || 0);
                            }, 0);
                            const distribucionBlur = calcularDistribucionConLimiteCaso(montoReclamado, porcientoCoaseguro, totalOtrasBlur, limiteCasoBlur);

                            setRows((prev) =>
                              prev.map((row) =>
                                Number(row.cobertura) === Number(r.cobertura) && row.secuencia === r.secuencia
                                  ? {
                                      ...row,
                                      diferenciaNoCubierta: distribucionBlur.diferenciaNoCubierta,
                                      montoCoaseguro: distribucionBlur.diferenciaNoCubierta,
                                      montoPagoAfiliado: distribucionBlur.diferenciaNoCubierta,
                                      montoPagoCobertura: distribucionBlur.montoPagoCobertura,
                                      excedenteCopago: distribucionBlur.diferenciaNoCubierta,
                                    }
                                  : row
                              )
                            );
                          }
                        }}
                        style={{ 
                          textAlign: "right",
                          cursor: Number(r.cobertura) === COBERTURA_OXIGENO ? "pointer" : "text"
                        }}
                        placeholder="0"
                      />
                    ) : (
                      <ReadonlyInput value={displayRD(r.montoReclamadoCobertura)} readOnly />
                    )}
                  </td>
                  <td>
                    <ReadonlyInput value={displayRD(r.montoPagoCobertura)} readOnly />
                  </td>
                  <td>
                    <ReadonlyInput value={displayRD(calcularDifNoCubierta(r))} readOnly />
                  </td>
                  <td>
                    <ReadonlyInput value={`${r.porcientoCobertura || 0}%`} readOnly />
                  </td>
                  <td>
                    <ActionsCell>
                      {eliminarCoberturaLoading && coberturaEliminandoRef.current === Number(r.cobertura) ? (
                        <LoadingOutlined
                          style={{
                            color: "#ef4444",
                            fontSize: 18,
                          }}
                          spin
                        />
                      ) : (
                      <DeleteOutlined
                          style={{
                            color: isDisabled || eliminarCoberturaLoading || !(r.esNueva || r.vieneDeHojaQuirurgica) ? "#ccc" : "#ef4444",
                            fontSize: 18,
                            cursor: isDisabled || eliminarCoberturaLoading || !(r.esNueva || r.vieneDeHojaQuirurgica) ? "not-allowed" : "pointer",
                          }}
                          onClick={isDisabled || eliminarCoberturaLoading || !(r.esNueva || r.vieneDeHojaQuirurgica) ? undefined : () => removeRow(r)}
                        title={r.esNueva || r.vieneDeHojaQuirurgica ? "Eliminar" : "No se puede eliminar una cobertura del detalle del ingreso"}
                      />
                      )}
                    </ActionsCell>
                  </td>
                </tr>
              );
              })}
              <tr>
                <td>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <Input
                      placeholder={coberturasCargando ? "Cargando..." : "Código"}
                      value={draft.cobertura || ""}
                      disabled={isDisabled || coberturasCargando}
                      onChange={(e) => {
                        const nuevoCodigo = Number(e.target.value.replace(/\D/g, "")) || 0;
                        setDraft((p) => ({
                          ...p,
                          cobertura: nuevoCodigo,
                          descripcionCobertura: "", // Limpiar descripción mientras se valida
                        }));
                        
                        // Validar el código digitado con debounce
                        if (nuevoCodigo > 0) {
                          validarCodigoDigitadoDebounced(nuevoCodigo);
                        }
                      }}
                    />
                    {coberturasCargando || codigoCoberturaBuscando ? (
                      <LoadingOutlined
                        style={{
                          fontSize: 16,
                          color: primary,
                        }}
                        spin
                      />
                    ) : (
                      <IconBtn
                        type="button"
                        title="Buscar coberturas"
                        onClick={isDisabled ? undefined : openModal}
                        disabled={isDisabled}
                        $color={primary}
                        $hover="#005a9a"
                        style={{
                          opacity: isDisabled ? 0.5 : 1,
                          cursor: isDisabled ? "not-allowed" : "pointer",
                        }}
                      >
                        <SearchOutlined style={{ fontSize: 16 }} />
                      </IconBtn>
                    )}
                  </div>
                </td>

                <td>
                  <ReadonlyInput value={draft.descripcionCobertura} readOnly />
                </td>

                <td>
                  <Select
                    value={draft.tipoAfiliado}
                    disabled={isDisabled}
                    onChange={(e) => {
                      const nuevoValor = e.target.value;

                      if (esPssNoMedico && esPaquete && nuevoValor === "MEDICO") {
                        setAlertConfig({
                          type: "error",
                          title: "¡Advertencia!",
                          message:
                            "No aplica cobertura para un reclamante médico en este centro bajo modalidad de Paquete. Para más información, por favor comuníquese con nuestro Centro de Atención al Cliente al 809-476-3535, opción 1 y luego opción 1.",
                          onConfirm: () => setShowAlert(false),
                        });
                        setShowAlert(true);
                        return;
                      }

                      setDraft((p) => ({ ...p, tipoAfiliado: nuevoValor, montoReclamadoCobertura: "" }));
                    }}
                  >
                    <option value="NO_MEDICO">No Médico</option>
                    <option value="MEDICO">Médico</option>
                  </Select>
                </td>

                <td>
                  <Input
                    type="text"
                    value={draft.codigoCentro}
                    disabled={isDisabled || consultarReclamantesLoading}
                    onChange={(e) => {
                      const valor = e.target.value.replace(/\D/g, "");
                      setDraft((p) => ({ ...p, codigoCentro: valor, nombreReclamante: "", montoReclamadoCobertura: "" }));
                      // Buscar reclamante automáticamente después de digitar
                      buscarReclamanteDebounced(valor, draft.tipoAfiliado);
                    }}
                    placeholder={consultarReclamantesLoading ? "Buscando..." : ""}
                  />
                </td>
                <td>
                  <ReadonlyInput value={draft.nombreReclamante} readOnly />
                </td>
                <td>
                  <ReadonlyInput 
                    value={Number(draft.cobertura) === COBERTURA_HABITACION ? displayRD(0) : displayRD(draft.limiteCobertura)} 
                    readOnly 
                  />
                </td>

                <td>
                  <Input
                    value={draft.cobertura ? frecuenciaInputValue : ""}
                    disabled={isDisabled || esCoberturaMontoAbierto(draft.cobertura) || draft.cobertura === COBERTURA_HABITACION}
                    readOnly={esCoberturaMontoAbierto(draft.cobertura) || draft.cobertura === COBERTURA_HABITACION}
                    title={
                      draft.cobertura === COBERTURA_HABITACION 
                        ? "La frecuencia de Habitación se calcula automáticamente con las fechas" 
                        : (esCoberturaMontoAbierto(draft.cobertura) ? "La frecuencia de coberturas con monto abierto siempre es 1" : "")
                    }
                    onChange={(e) => {
                      // Si es cobertura con monto abierto o 1064, no permitir cambios
                      if (esCoberturaMontoAbierto(draft.cobertura) || draft.cobertura === COBERTURA_HABITACION) {
                        return;
                      }
                      const raw = e.target.value.replace(/\D/g, "");
                      setFrecuenciaInputValue(raw);

                      if (frecuenciaDebounceRef.current) {
                        clearTimeout(frecuenciaDebounceRef.current);
                      }
                      frecuenciaDebounceRef.current = setTimeout(() => {
                        frecuenciaDebounceRef.current = null;
                        const nuevaFrecuencia = Number(raw) || 0;

                        // La frecuencia no puede ser 0
                        if (nuevaFrecuencia === 0) {
                          setFrecuenciaInputValue(String(draft.frecuencia ?? "1"));
                          setAlertConfig({
                            type: "error",
                            title: "¡Error!",
                            message: "La frecuencia no puede ser 0.",
                            onConfirm: () => setShowAlert(false),
                          });
                          setShowAlert(true);
                          return;
                        }

                        // Validación general de frecuencia máxima (incluye 1064 y coberturas normales)
                        if (draft.frecuenciaMaxima !== null && draft.frecuenciaMaxima !== undefined && nuevaFrecuencia > draft.frecuenciaMaxima) {
                          setFrecuenciaInputValue(String(draft.frecuencia ?? ""));
                          const mensajeFrecuencia =
                            draft.cobertura === COBERTURA_HABITACION
                              ? `La frecuencia no puede ser mayor a ${draft.frecuenciaMaxima} días (diferencia entre fecha de ingreso y egreso).`
                              : `La frecuencia no puede ser mayor a ${draft.frecuenciaMaxima}.`;
                          setAlertConfig({
                            type: "error",
                            title: "¡Error!",
                            message: mensajeFrecuencia,
                            onConfirm: () => setShowAlert(false),
                          });
                          setShowAlert(true);
                          return;
                        }

                        const limiteContrato = Number(draft.limiteCobertura) || 0;
                        // Al cambiar la frecuencia, setear monto reclamado = límite contrato × frecuencia
                        // EXCEPTO para cobertura 1064 (Habitación) que no calcula automáticamente
                        const montoReclamado = draft.cobertura === COBERTURA_HABITACION 
                          ? (Number(draft.montoReclamadoCobertura) || 0)
                          : (limiteContrato * nuevaFrecuencia);

                        setFrecuenciaInputValue(String(nuevaFrecuencia));

                        // Calcular distribución ARS/Afiliado considerando límite por caso
                        const tienePorcientoCobertura = draft.porcientoCobertura !== undefined && draft.porcientoCobertura !== null;
                        if (tienePorcientoCobertura) {
                          const porcientoCoaseguro = Number(draft.porcientoCobertura);
                          const totalCuentaOtras = rows.reduce((acc, r) => acc + (Number(r.montoReclamadoCobertura) || 0), 0);
                          const limiteCasoVal = Number(encabezado?.limiteCaso) || 0;
                          const distribucion = calcularDistribucionConLimiteCaso(montoReclamado, porcientoCoaseguro, totalCuentaOtras, limiteCasoVal);

                          setDraft((p) => ({
                            ...p,
                            frecuencia: nuevaFrecuencia,
                            montoReclamadoCobertura: montoReclamado,
                            diferenciaNoCubierta: distribucion.diferenciaNoCubierta,
                            montoCoaseguro: distribucion.diferenciaNoCubierta,
                            montoPagoAfiliado: distribucion.diferenciaNoCubierta,
                            montoPagoCobertura: distribucion.montoPagoCobertura,
                            excedenteCopago: distribucion.diferenciaNoCubierta,
                          }));
                        } else {
                          setDraft((p) => ({
                            ...p,
                            frecuencia: nuevaFrecuencia,
                            montoReclamadoCobertura: montoReclamado,
                          }));
                        }

                      }, 400);
                    }}
                  />
                </td>

                <td>
                  <Input
                    value={draft.montoReclamadoCobertura || ""}
                    disabled={isDisabled}
                    style={
                      errorMontoReclamado 
                        ? { borderColor: "#ef4444", borderWidth: "2px" } 
                        : Number(draft.cobertura) === COBERTURA_OXIGENO 
                          ? { cursor: "pointer" } 
                          : {}
                    }
                    onClick={() => {
                      // Si es cobertura de oxígeno, abrir calculadora
                      if (Number(draft.cobertura) === COBERTURA_OXIGENO) {
                        setCoberturaSeleccionada({
                          cobertura: draft.cobertura,
                          descripcionCobertura: draft.descripcionCobertura,
                          tipoCobertura: draft.tipoCobertura,
                          limite: draft.limiteCobertura,
                          porcientoCobertura: draft.porcientoCobertura, // Guardar el % Coaseguro
                        });
                        // Pre-cargar valores actuales del draft
                        setMontoReclamadoOxigeno(draft.montoReclamadoCobertura || "");
                        setCantidadLitros("");
                        setContratoTipo(TIPO_CONTRATO.LITRO);
                        setFilaOxigenoEditando(null); // No estamos editando una fila, es el draft
                        setIsModalOxigenoOpen(true);
                      }
                    }}
                    onChange={(e) => {
                      const valorString = e.target.value.replace(/[^\d.]/g, "");
                      
                      // Validación: permitir campo vacío, números, y punto decimal
                      if (valorString === "") {
                        setDraft((p) => ({ ...p, montoReclamadoCobertura: "" }));
                        return;
                      }
                      
                      // Validar formato: múltiples puntos, demasiados decimales, demasiados enteros
                      const tieneMultiplesPuntos = (valorString.match(/\./g) || []).length > 1;
                      const partesDecimal = valorString.split(".");
                      const tieneDemasiadosDecimales = partesDecimal[1] && partesDecimal[1].length > 2;
                      const tieneDemasiadosEnteros = partesDecimal[0] && partesDecimal[0].length > 10;
                      
                      if (tieneMultiplesPuntos || tieneDemasiadosDecimales || tieneDemasiadosEnteros) {
                        return; // Bloquear entrada inválida
                      }
                      
                      const valorIngresado = Number(valorString) || 0;
                      const frecuencia = Number(draft.frecuencia) || 1;
                      const limiteContrato = Number(draft.limiteCobertura) || 0;
                      const montoMaximo = frecuencia * limiteContrato;
                      const esMontoAbierto = esCoberturaMontoAbierto(draft.cobertura);

                      let nuevoMonto = valorIngresado;
                      let nuevoMontoString = valorString;
                      // Coberturas con monto abierto no tienen tope frecuencia×límite; el resto sí
                      if (!esMontoAbierto && valorIngresado > montoMaximo) {
                        nuevoMonto = montoMaximo;
                        nuevoMontoString = String(montoMaximo);
                        setAlertConfig({
                          type: "info",
                          title: "¡Atención!",
                          message: `El monto reclamado no puede ser mayor a ${montoMaximo.toLocaleString("es-DO")} (Frecuencia × Límite Contrato).`,
                          onConfirm: () => setShowAlert(false),
                        });
                        setShowAlert(true);
                      }

                      // Limpiar error si el monto es mayor a 0
                      if (nuevoMonto > 0 && errorMontoReclamado) {
                        setErrorMontoReclamado(false);
                      }

                      // Validar tope de cobertura 1096 antes de actualizar el draft
                      if (!validarTopeCobertura1096(nuevoMonto, draft.cobertura, null)) {
                        return; // No actualizar si supera el tope
                      }

                      const tienePorcientoCobertura = draft.porcientoCobertura !== undefined && 
                                                      draft.porcientoCobertura !== null;
                      
                      if (tienePorcientoCobertura) {
                        // Calcular distribución considerando límite por caso
                        const porcientoCoaseguro = Number(draft.porcientoCobertura);
                        const totalCuentaOtras = rows.reduce((acc, r) => acc + (Number(r.montoReclamadoCobertura) || 0), 0);
                        const limiteCaso = Number(encabezado?.limiteCaso) || 0;
                        const distribucion = calcularDistribucionConLimiteCaso(nuevoMonto, porcientoCoaseguro, totalCuentaOtras, limiteCaso);

                        setDraft((p) => ({
                          ...p,
                          montoReclamadoCobertura: nuevoMontoString,
                          diferenciaNoCubierta: distribucion.diferenciaNoCubierta,
                          montoCoaseguro: distribucion.diferenciaNoCubierta,
                          montoPagoAfiliado: distribucion.diferenciaNoCubierta,
                          montoPagoCobertura: distribucion.montoPagoCobertura,
                          excedenteCopago: distribucion.diferenciaNoCubierta,
                        }));
                      } else {
                        // Sin % coaseguro, solo actualizar el monto
                        setDraft((p) => ({
                          ...p,
                          montoReclamadoCobertura: nuevoMontoString,
                        }));
                      }

                      // Validar límite por caso con el nuevo monto del draft (se muestra una sola vez gracias al ref)
                      const totalConNuevoDraft = rows.reduce((acc, r) => acc + (Number(r.montoReclamadoCobertura) || 0), 0) + nuevoMonto;
                      validarLimitePorCaso(totalConNuevoDraft);
                    }}
                  />
                </td>

                <td>
                  <ReadonlyInput value={displayRD(draft.montoPagoCobertura)} readOnly />
                </td>
                <td>
                  <ReadonlyInput value={displayRD(calcularDifNoCubierta(draft))} readOnly />
                </td>
                <td>
                  <ReadonlyInput value={`${draft.porcientoCobertura || 0}%`} readOnly />
                </td>

                <td>
                  <ActionsCell>
                    <PlusCircleOutlined
                      style={{
                        color: isDisabled ? "#ccc" : "#2563eb",
                        fontSize: 20,
                        cursor: isDisabled ? "not-allowed" : "pointer",
                      }}
                      onClick={
                        isDisabled
                          ? undefined
                          : () => {
                              if (!draft.cobertura) {
                          openModal();
                          return;
                        }
                        addDraftRow();
                            }
                      }
                      title="Agregar"
                    />
                    <DeleteOutlined
                      style={{
                        color: isDisabled ? "#ccc" : "#ef4444",
                        fontSize: 18,
                        cursor: isDisabled ? "not-allowed" : "pointer",
                      }}
                      onClick={
                        isDisabled
                          ? undefined
                          : () => {
                              const fechaActual = new Date().toISOString();
                        setDraft((p) => ({
                          ...p,
                                cobertura: 0,
                                descripcionCobertura: "",
                                tipoCobertura: 2,
                                frecuencia: 1,
                                montoReclamadoCobertura: 0,
                                limiteCobertura: 0,
                                porcientoCobertura: 0,
                                montoPagoCobertura: 0,
                                montoPagoAfiliado: 0,
                                excedenteCopago: 0,
                                reserva: 0,
                                fechaServicio: fechaActual,
                              }));
                            }
                      }
                      title="Limpiar fila"
                    />
                  </ActionsCell>
                </td>
              </tr>
            </tbody>
          </CoveragesTable>
        </ScrollX>

        <FooterRow>
          <VoucherGroup>
            <VoucherButton
              type="button"
              $primary={primary}
              disabled={vouchersDisabled}
              onClick={handleVoucherTermico}
            >
              Voucher Térmico
            </VoucherButton>
            <VoucherButton
              type="button"
              $primary={primary}
              disabled={vouchersDisabled}
              onClick={handleVoucherMatricial}
            >
              Voucher Matricial
            </VoucherButton>
            <VoucherButton
              type="button"
              $primary={primary}
              disabled={vouchersDisabled}
              onClick={handleVoucherMedico}
            >
              Voucher Médico
            </VoucherButton>
          </VoucherGroup>

          <TotalsBox>
            <div>
              <span>Total ARS:</span>
              <TotalValue>{formatRD(totals.totalARS)}</TotalValue>
            </div>
            <div>
              <span>Total Asegurado:</span>
              <TotalValue>{formatRD(totals.totalAsegurado)}</TotalValue>
            </div>
            <div>
              <span>Total Reclamado:</span>
              <TotalValue>{formatRD(totals.totalReclamado)}</TotalValue>
            </div>
            <IconBtn
              type="button"
              $color={primary}
              onClick={() => setIsDetalleReclamantesOpen(true)}
              title="Ver detalle por reclamante"
              style={{ marginLeft: "10px" }}
            >
              <InfoCircleOutlined style={{ fontSize: "18px" }} />
            </IconBtn>
          </TotalsBox>
        </FooterRow>
      </TableOuter>

      <CustomSeparator height="15px" />

      <ActionsBar>
        <PrimaryAction
          type="button"
          disabled={isDisabled || enviarReclamacionLoading || validarIngresoExistenteLoading}
          onClick={handleEnviarReclamacion}
        >
          {validarIngresoExistenteLoading ? "Validando..." : enviarReclamacionLoading ? "Enviando..." : "Enviar Reclamación"}
        </PrimaryAction>
        <SecondaryAction
          type="button"
          onClick={() => {}}
        >
          Cancelar
        </SecondaryAction>
      </ActionsBar>

      <CustomSeparator height="15px" />

      <CustomModal
        isOpen={isModalOpen}
        onClose={closeModal}
        width="765px"
        maxWidth="95vw"
      >
        <ModalBody style={{ minWidth: "894px", minHeight: "666px" }}>
          <ModalHeader>
            <ModalTitle>Cobertura contratada</ModalTitle>
            <ModalSearch
              placeholder="Buscar por código o descripción"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </ModalHeader>

          <CustomSeparator height="8px" />

          <div style={{ minHeight: "553px", position: "relative" }}>
            <CheckRender allowed={consultarCoberturasCierreCuentaLoading}>
              <div style={{ 
                display: "flex", 
                flexDirection: "column", 
                alignItems: "center", 
                justifyContent: "center", 
                minHeight: "553px",
                gap: "17px"
              }}>
                <Spinner size={50} colorBorderTop={primary} />
                <p style={{ color: "#666", fontSize: "14px" }}>Cargando coberturas...</p>
              </div>
            </CheckRender>

            <CheckRender allowed={!consultarCoberturasCierreCuentaLoading && coberturasFuente.length === 0}>
              <div style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                minHeight: "553px",
              }}>
                <strong>No hay coberturas para mostrar.</strong>
              </div>
            </CheckRender>

            <CheckRender allowed={!consultarCoberturasCierreCuentaLoading && coberturasFuente.length > 0}>
              <CustomTable
                showPagination={false}
                pageSize={ITEMS_PER_PAGE}
                columns={[
                  { title: "Código", dataIndex: "cobertura", key: "cobertura" },
                  {
                    title: "Descripción",
                    dataIndex: "descripcionCobertura",
                    key: "descripcionCobertura",
                  },
                  {
                    title: "Acción",
                    key: "accion",
                    render: (_, record) => (
                      <SelectBtn
                        type="button"
                        disabled={!tieneFechaEgreso}
                        onClick={() => {
                          if (!tieneFechaEgreso) {
                            setShowAlert(false);
                            setTimeout(() => {
                              setAlertConfig({
                                type: "info",
                                title: "¡Atención!",
                                message: "Debe establecer la fecha de egreso antes de seleccionar coberturas.",
                                onConfirm: () => setShowAlert(false),
                              });
                              setShowAlert(true);
                            }, 100);
                            return;
                          }
                          onSelectCobertura(record);
                        }}
                      >
                        Seleccionar
                      </SelectBtn>
                    ),
                  },
                ]}
                dataSource={(Array.isArray(pagedCoberturas) ? pagedCoberturas : []).map(
                  (x, idx) => ({
                    ...x,
                    key: `${x?.cobertura ?? "cob"}-${idx}`,
                  })
                )}
              />
            </CheckRender>
          </div>

          <CustomSeparator height="8px" />

          <CheckRender allowed={totalRegistros > 0}>
            <Pagination
              currentPage={modalPage}
              itemsPerPage={ITEMS_PER_PAGE}
              totalItems={totalRegistros}
              onPageChange={setModalPage}
              disabled={consultarCoberturasCierreCuentaLoading}
            />
          </CheckRender>
        </ModalBody>
      </CustomModal>

      <CustomModal
        isOpen={isDetalleReclamantesOpen}
        onClose={() => setIsDetalleReclamantesOpen(false)}
        width="800px"
        maxWidth="95vw"
      >
        <ModalBody>
          <ModalHeader>
            <ModalTitle>Detalle de Totales por Reclamante</ModalTitle>
          </ModalHeader>

          <CustomSeparator height="10px" />

          <CustomTable
            showPagination={false}
            pageSize={10}
            columns={[
              {
                title: "Código Reclamante",
                dataIndex: "codigoReclamante",
                key: "codigoReclamante",
                width: "20%",
              },
              {
                title: "Reclamante",
                dataIndex: "nombreReclamante",
                key: "nombreReclamante",
                width: "30%",
              },
              {
                title: "Total Reclamado",
                key: "totalReclamado",
                width: "20%",
                render: (_, record) => formatRD(record.totalReclamado),
              },
              {
                title: "Total ARS",
                key: "totalARS",
                width: "15%",
                render: (_, record) => formatRD(record.totalARS),
              },
              {
                title: "Total Asegurado",
                key: "totalAsegurado",
                width: "15%",
                render: (_, record) => formatRD(record.totalAsegurado),
              },
            ]}
            dataSource={(Array.isArray(totalesPorReclamante) ? totalesPorReclamante : []).map(
              (item, idx) => ({
                ...item,
                key: `reclamante-${idx}`,
              })
            )}
          />

          <CheckRender allowed={totalesPorReclamante.length === 0}>
            <div style={{ padding: 10 }}>
              <strong>No hay reclamantes para mostrar.</strong>
            </div>
          </CheckRender>

          <CustomSeparator height="10px" />

          <div style={{ display: "flex", justifyContent: "flex-end", padding: "10px 0" }}>
            <SecondaryAction
              type="button"
              onClick={() => setIsDetalleReclamantesOpen(false)}
            >
              Cerrar
            </SecondaryAction>
          </div>
        </ModalBody>
      </CustomModal>

      <CustomModal
        isOpen={isModalOxigenoOpen}
        onClose={() => setIsModalOxigenoOpen(false)}
        width="900px"
        maxWidth="95vw"
      >
        <ModalBody>
          <ModalHeader>
            <ModalTitle>Calculadora de coberturas de oxígeno</ModalTitle>
          </ModalHeader>

          <CustomSeparator height="10px" />

          <CalculadoraOxigeno
            prestador={prestadorNombre}
            tarifa={coberturaSeleccionada?.limite}
            contratoTipo={contratoTipo}
            cantidadLitros={cantidadLitros}
            montoReclamado={montoReclamadoOxigeno}
            onChangeContratoTipo={onChangeTipoContratoOxigeno}
            onChangeCantidadLitros={handleChangeLitros}
            onChangeMontoReclamado={setMontoReclamadoOxigeno}
            onCalcular={realizarCalculoOxigeno}
            onLimpiar={LimpiarCalculoOxigeno}
            onCancelar={() => {
              setIsModalOxigenoOpen(false);
              setFilaOxigenoEditando(null);
              LimpiarCalculoOxigeno();
              setContratoTipo(TIPO_CONTRATO.LITRO);
              limpiarCalcularOxigeno?.();
            }}
            onAplicar={agregarOxigeno}
          />
        </ModalBody>
      </CustomModal>

      <CheckRender allowed={showAlert}>
        <CustomAlert {...alertConfig} />
      </CheckRender>

      {/* Modal de selección de reclamante para voucher */}
      <CustomModal
        isOpen={showVoucherModal}
        onClose={() => setShowVoucherModal(false)}
        width="450px"
        maxWidth="90vw"
      >
        <div style={{ padding: "20px" }}>
          <h3 style={{ margin: "0 0 20px 0", fontSize: "18px", fontWeight: "600" }}>
            Generar Voucher {voucherConfig.tipo === 'termico' ? 'Térmico' : voucherConfig.tipo === 'matricial' ? 'Matricial' : 'Médico'}
          </h3>

          {voucherConfig.reclamantes.length === 0 ? (
            <div style={{ textAlign: "center", padding: "20px 0", color: "#666" }}>
              <InfoCircleOutlined style={{ fontSize: "36px", marginBottom: "12px", color: "#999" }} />
              <p style={{ margin: 0 }}>No hay reclamantes {voucherConfig.tipo === 'medico' ? 'médicos' : 'no médicos'} en la tabla de coberturas.</p>
            </div>
          ) : (
            <>
              <div style={{ marginBottom: "16px" }}>
                <label style={{ display: "block", marginBottom: "6px", fontWeight: "600", fontSize: "14px" }}>
                  Seleccione un reclamante:
                </label>
                <Select
                  value={voucherConfig.reclamanteSeleccionado || ""}
                  onChange={(e) => setVoucherConfig(prev => ({ ...prev, reclamanteSeleccionado: e.target.value }))}
                  style={{ width: "100%", padding: "8px", fontSize: "14px" }}
                >
                  <option value="">-- Seleccione --</option>
                  {voucherConfig.reclamantes.map((rec) => (
                    <option key={rec.codigo} value={rec.codigo}>
                      {rec.codigo} - {rec.nombre}
                    </option>
                  ))}
                </Select>
              </div>

              {voucherConfig.tipo === 'medico' && (
                <div style={{ marginBottom: "16px" }}>
                  <label style={{ display: "flex", alignItems: "center", cursor: "pointer", fontSize: "14px" }}>
                    <input
                      type="checkbox"
                      checked={voucherConfig.esMatricial}
                      onChange={(e) => setVoucherConfig(prev => ({ ...prev, esMatricial: e.target.checked }))}
                      style={{ marginRight: "8px" }}
                    />
                    <span>¿Es matricial?</span>
                  </label>
                </div>
              )}

              <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end", marginTop: "24px" }}>
                <SecondaryAction
                  type="button"
                  onClick={() => setShowVoucherModal(false)}
                  disabled={descargandoVoucher}
                  style={{ padding: "8px 16px", fontSize: "14px" }}
                >
                  Cancelar
                </SecondaryAction>
                <PrimaryAction
                  type="button"
                  onClick={generarVoucher}
                  disabled={descargandoVoucher || !voucherConfig.reclamanteSeleccionado}
                  style={{
                    padding: "8px 16px",
                    fontSize: "14px",
                    opacity: (descargandoVoucher || !voucherConfig.reclamanteSeleccionado) ? 0.6 : 1,
                    cursor: (descargandoVoucher || !voucherConfig.reclamanteSeleccionado) ? 'not-allowed' : 'pointer',
                  }}
                >
                  {descargandoVoucher ? (
                    <>
                      <LoadingOutlined style={{ marginRight: "8px" }} spin />
                      Generando...
                    </>
                  ) : (
                    'Generar'
                  )}
                </PrimaryAction>
              </div>
            </>
          )}
        </div>
      </CustomModal>
    </>
  );
};

const mapStateToProps = (state) => {
  const slice = state.egreso || state.hospitalizacion || {};
  return {
    consultarCoberturasCierreCuentaData: slice.consultarCoberturasCierreCuentaData,
    authUserData: state.auth?.authUserData,
    eliminarCoberturaData: slice.eliminarCoberturaData,
    eliminarCoberturaLoading: slice.eliminarCoberturaLoading,
    eliminarCoberturaError: slice.eliminarCoberturaError,
    validarCoberturaData: slice.validarCoberturaData,
    validarCoberturaLoading: slice.validarCoberturaLoading,
    validarCoberturaError: slice.validarCoberturaError,
    consultarIngresosPssData: slice.consultarIngresosPssData,
    consultarReclamantesData: slice.consultarReclamantesData,
    consultarReclamantesLoading: slice.consultarReclamantesLoading,
    consultarReclamantesError: slice.consultarReclamantesError,
    calcularOxigenoData: slice.calcularOxigenoData,
    calcularOxigenoLoading: slice.calcularOxigenoLoading,
    calcularOxigenoError: slice.calcularOxigenoError,
    validarIngresoExistenteData: slice.validarIngresoExistenteData,
    validarIngresoExistenteLoading: slice.validarIngresoExistenteLoading,
    validarIngresoExistenteError: slice.validarIngresoExistenteError,
    enviarReclamacionData: slice.enviarReclamacionData,
    enviarReclamacionLoading: slice.enviarReclamacionLoading,
    enviarReclamacionError: slice.enviarReclamacionError,
  };
};

const mapDispatchToProps = {
  eliminarCobertura: EgresosActions.eliminarCobertura,
  limpiarEliminarCobertura: EgresosActions.limpiarEliminarCobertura,
  validarCobertura: EgresosActions.validarCobertura,
  limpiarValidarCobertura: EgresosActions.limpiarValidarCobertura,
  consultarReclamantes: EgresosActions.consultarReclamantes,
  limpiarConsultarReclamantes: EgresosActions.limpiarConsultarReclamantes,
  calcularOxigeno: EgresosActions.calcularOxigeno,
  limpiarCalcularOxigeno: EgresosActions.limpiarCalcularOxigeno,
  validarIngresoExistente: EgresosActions.validarIngresoExistente,
  limpiarValidarIngresoExistente: EgresosActions.limpiarValidarIngresoExistente,
  enviarReclamacion: EgresosActions.enviarReclamacion,
  limpiarEnviarReclamacion: EgresosActions.limpiarEnviarReclamacion,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(CoberturasCierreCuentaTabla);



